/**
 * AssetManagementPage.jsx
 *
 * Main operational page for the Asset Management system.
 * Provides a comprehensive interface for viewing, creating, editing, and managing
 * physical assets across all properties the user has access to.
 *
 * ── Architecture Overview ────────────────────────────────────────────────────
 *
 * This page is a single large component backed by several sub-components and
 * helper functions defined in the same file:
 *
 *   AssetManagementPage  — root component; owns all state and data-fetching
 *   ├── DraggableAssetRow — table row that participates in @dnd-kit drag events;
 *   │                       also renders an inline quick-service form
 *   └── DroppableGroupCard — sidebar card that accepts dragged assets as members
 *
 * ── Data Flow ────────────────────────────────────────────────────────────────
 *
 *   1. On mount: fetchAssets, fetchCategories, fetchReferenceData,
 *      fetchProperties, and fetchServiceTypes are called in parallel.
 *   2. Filters (property, category, status, inactive toggle) are held in state.
 *      Changing any filter resets the page counter and re-fetches assets from
 *      the server-side paginated endpoint.
 *   3. Client-side filtering (search query + dashboard card filter) is applied
 *      AFTER the server returns results, giving instant feedback without a
 *      network round-trip for text search.
 *   4. SignalR keeps the asset list live when other users make changes.
 *
 * ── Service Record Workflow (state machine) ──────────────────────────────────
 *
 *   Pending  ──(submit)──▶  Submitted  ──(approve)──▶  Approved
 *                                      └──(reject)───▶  Rejected
 *
 *   Each transition hits a PATCH endpoint and then re-fetches the asset detail
 *   so the UI always shows the latest state from the server.
 *
 * ── Drag-and-Drop ────────────────────────────────────────────────────────────
 *
 *   Uses @dnd-kit/core.  The DndContext wraps the entire page so that
 *   DraggableAssetRow (draggable) and DroppableGroupCard (droppable) can
 *   communicate through the context without prop-drilling.
 *   A PointerSensor with an 8-px activation threshold prevents accidental drags
 *   when the user clicks action buttons inside the row.
 *
 * @module OperationsPages/AssetManagementPage
 */

import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import useAssetSignalR from '@/hooks/useAssetSignalR';
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter
} from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import {
    FaPlus, FaEdit, FaEye, FaTrashAlt, FaUndo,
    FaSpinner, FaSearch, FaWrench, FaHistory, FaTimes,
    FaExclamationTriangle, FaUpload, FaDownload, FaFileAlt,
    FaBuilding, FaTag, FaCog, FaCogs, FaInfoCircle, FaMapMarkerAlt,
    FaCalendarCheck, FaShieldAlt, FaMoneyBillWave, FaStickyNote,
    FaLayerGroup, FaObjectGroup, FaGripVertical, FaChevronDown,
    FaChevronRight, FaChevronLeft, FaBolt, FaToolbox, FaFilter, FaSave
} from 'react-icons/fa';
import '@/styles/assetManagementStyles.scss';
import * as Sentry from '@sentry/react';

/**
 * AssetManagementPage Component
 *
 * Renders the main asset management interface with listing, filtering,
 * and CRUD operations for assets across accessible properties.
 *
 * All data-fetching callbacks are defined with useCallback so their identities
 * are stable across renders. This is important because several of them are used
 * as useEffect dependencies — an unstable reference would cause infinite loops.
 */
const AssetManagementPage = () => {
    const { apiFetch, user, hasPermission } = useAuth();
    const navigate = useNavigate();

    // ── SignalR real-time updates ────────────────────────────────────────────
    //
    // useAssetSignalR connects to the /hubs/assets SignalR hub and invokes the
    // callbacks below when the server broadcasts asset change events.
    //
    // WHY useMemo for signalRCallbacks?
    //   The hook takes the callbacks object as a dependency.  If we defined the
    //   object inline, a new reference would be created on every render,
    //   causing the hook to re-subscribe on every render.  useMemo gives us a
    //   stable reference that only changes when `user` changes.
    //
    // WHY refs for fetchAssetsRef / fetchDashboardStatsRef?
    //   SignalR callbacks are registered once inside the hook.  If we captured
    //   fetchAssets directly in the closure, we would hold a stale reference to
    //   an old version of the function.  Storing the latest version in a ref
    //   means the SignalR handler always calls the current function without
    //   needing to re-register the listener.

    const signalRCallbacks = useMemo(() => ({
        onAssetChanged: (data) => {
            // Skip events triggered by the current user to avoid double-refresh
            // when the user's own save operation broadcasts back to them.
            if (data.changedByUserId === (user?.userID || user?.id)) return;
            toast.info(`Asset "${data.assetName || 'unknown'}" was ${data.type?.replace(/([A-Z])/g, ' $1').trim().toLowerCase()} by another user`);
            // Refresh the asset list and dashboard stats
            fetchAssetsRef.current?.();
            fetchDashboardStatsRef.current?.();
        }
    }), [user]);

    const { isConnected: assetSignalRConnected, joinProperty, leaveProperty } = useAssetSignalR(signalRCallbacks);

    // Refs to hold the latest fetch functions for SignalR callbacks (avoids stale closures)
    const fetchAssetsRef = useRef(null);
    const fetchDashboardStatsRef = useRef(null);

    // ── State ────────────────────────────────────────────────────────────────
    //
    // State is grouped by concern so that related pieces are easy to find
    // and so that future refactoring into context/reducer slices is straight-
    // forward. Each group is described below.

    // ── Asset listing state ──────────────────────────────────────────────────
    // `assets` holds the current page of assets returned by the server.
    // `loading` gates the loading spinner / empty-state messages.
    // `error` holds a user-facing error string when the fetch fails.
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // ── Filter state ─────────────────────────────────────────────────────────
    // These four values are sent to the server as query-string parameters so
    // that filtering and pagination happen on the database, not in the browser.
    // `searchQuery` is the only exception — it is applied client-side after the
    // page loads (see `filteredAssets` below) to allow instant, zero-latency
    // text search without a round trip.
    // `showInactive` toggles whether soft-deleted assets are included.
    const [filterPropertyId, setFilterPropertyId] = useState('');
    const [filterCategoryId, setFilterCategoryId] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [showInactive, setShowInactive] = useState(false);

    // ── Reference data state ─────────────────────────────────────────────────
    // Loaded once on mount and used to populate form dropdowns.
    // `categories` — asset category/sub-category tree.
    // `properties` — properties the current user has access to.
    // `referenceData` — enum lists: statuses, interval units, document types,
    //                   service record types (from /api/AssetManagement/reference-data).
    // `usersList` — portal users available as asset owner/manager candidates
    //               (fetched alongside workflow-rules reference data).
    const [categories, setCategories] = useState([]);
    const [properties, setProperties] = useState([]);
    const [referenceData, setReferenceData] = useState(null);
    const [usersList, setUsersList] = useState([]);

    // ── Detail / Edit modal state ────────────────────────────────────────────
    // `selectedAsset` — the full asset object (with serviceRecords, auditLogs,
    //                   documents) loaded from GET /assets/{id}.
    // `showDetailModal` — controls the read-only detail view.
    // `showCreateModal` — controls both the create *and* edit forms (same modal,
    //                     distinguished by whether formData.assetId is set).
    // `showServiceModal` — controls the "Add Service Record" overlay that opens
    //                      from within the detail modal.
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showServiceModal, setShowServiceModal] = useState(false);

    // ── Asset create/edit form state ─────────────────────────────────────────
    // `formData` mirrors the AssetRequest DTO.  It is initialised from
    //  getEmptyFormData() and populated by handleEdit() when editing.
    // `saving` is true while the API call is in-flight, disabling the Save
    //  button to prevent double-submission.
    const [formData, setFormData] = useState(getEmptyFormData());
    const [saving, setSaving] = useState(false);

    // ── Service record form state ────────────────────────────────────────────
    // Shared between the full-modal service form (showServiceModal) and the
    // inline quick-service form rendered inside DraggableAssetRow.
    const [serviceFormData, setServiceFormData] = useState(getEmptyServiceForm());

    // ── Detail view tab state ────────────────────────────────────────────────
    // Controls which tab is active inside the asset detail modal:
    // 'details' | 'services' | 'audit' | 'documents' | 'schedules'
    const [detailTab, setDetailTab] = useState('details');

    // ── Document upload state ────────────────────────────────────────────────
    // `uploadingDocument` shows a spinner on the Upload button while the
    //  multipart POST is in-flight.
    // `documentType` / `documentNotes` are form fields for the upload form.
    // `fileInputRef` is a hidden <input type="file"> triggered programmatically
    //  so we can style the upload button however we like.
    const [uploadingDocument, setUploadingDocument] = useState(false);
    const [documentType, setDocumentType] = useState('ServiceCertificate');
    const [documentNotes, setDocumentNotes] = useState('');
    const fileInputRef = useRef(null);

    // ── MDA tenant search state ──────────────────────────────────────────────
    // Powers the live-search autocomplete for linking an asset to an MDA tenant.
    // `tenantSearchResults` — list returned by the server after each debounced query.
    // `tenantSearchLoading` — shows a loading indicator inside the dropdown.
    // `showTenantDropdown` — controls dropdown visibility.
    // `tenantSearchRef` — ref on the text input, used for outside-click detection.
    // `tenantDropdownRef` — ref on the dropdown list, used for outside-click detection.
    const [tenantSearchResults, setTenantSearchResults] = useState([]);
    const [tenantSearchLoading, setTenantSearchLoading] = useState(false);
    const [showTenantDropdown, setShowTenantDropdown] = useState(false);
    const tenantSearchRef = useRef(null);
    const tenantDropdownRef = useRef(null);

    // ── MDA rentable unit search state ───────────────────────────────────────
    // Mirrors the tenant search pattern for linking an asset to a specific
    // rentable unit (e.g. Shop 1A in a mall). When a unit is selected the
    // associated tenant is auto-linked, and vice-versa (see auto-link pattern
    // in selectTenant / selectRentableUnit).
    // `rentableUnitSearch` holds the text-input value separately from
    //  formData.rentableUnitName so we can clear/update them independently.
    const [rentableUnitResults, setRentableUnitResults] = useState([]);
    const [rentableUnitLoading, setRentableUnitLoading] = useState(false);
    const [showRentableUnitDropdown, setShowRentableUnitDropdown] = useState(false);
    const [rentableUnitSearch, setRentableUnitSearch] = useState('');
    const rentableUnitDropdownRef = useRef(null);

    // ── Asset group state ────────────────────────────────────────────────────
    // Groups allow multiple assets to be serviced together in a single bulk
    // service event. They are scoped to a property.
    // `groups` — list of groups for the currently filtered property.
    // `showGroupModal` — create/edit group dialog.
    // `showBulkServiceModal` — bulk service record creation dialog.
    // `selectedGroup` — fully-loaded group (with members) for the detail panel.
    // `groupFormData` / `bulkServiceFormData` — form state for their respective modals.
    // `excludedAssetIds` — members opted out of the current bulk service event.
    const [groups, setGroups] = useState([]);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [showBulkServiceModal, setShowBulkServiceModal] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [groupFormData, setGroupFormData] = useState(getEmptyGroupForm());
    const [bulkServiceFormData, setBulkServiceFormData] = useState(getEmptyBulkServiceForm());
    const [excludedAssetIds, setExcludedAssetIds] = useState([]);

    // ── Service record workflow state ────────────────────────────────────────
    // Tracks the approve/reject flow for individual and bulk service records.
    // `expandedRecordId` — which service record card is currently expanded in
    //   the services tab (only one at a time; null = all collapsed).
    // `showRejectModal` / `rejectTargetId` / `rejectTargetBulkRef` — state for
    //   the rejection reason modal. bulkRef is non-null when rejecting an entire
    //   bulk batch.
    // `rejectionReason` — controlled textarea inside the rejection modal.
    // `workflowLoading` — disables workflow action buttons while a PATCH is in-flight.
    const [expandedRecordId, setExpandedRecordId] = useState(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectTargetId, setRejectTargetId] = useState(null);
    const [rejectTargetBulkRef, setRejectTargetBulkRef] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [workflowLoading, setWorkflowLoading] = useState(false);

    // ── Asset selection state ────────────────────────────────────────────────
    // Multi-select is used for the "Add to Group" batch operation.
    // `selectedAssetIds` — set of currently checked asset IDs.
    // `showGroupPicker` — the inline dropdown listing available groups, shown
    //   above the table when at least one asset is selected.
    const [selectedAssetIds, setSelectedAssetIds] = useState([]);
    const [showGroupPicker, setShowGroupPicker] = useState(false);

    // ── Dashboard summary state ──────────────────────────────────────────────
    // `dashboardStats` — aggregate counts returned by GET /api/AssetManagement/dashboard.
    // `dashboardFilter` — when a user clicks a summary card, this is set to
    //   the card's key ('overdue' | 'dueSoon' | 'warrantyExpired' | 'underRepair').
    //   The filteredAssets computation then applies an additional client-side
    //   predicate to highlight only those assets.
    const [dashboardStats, setDashboardStats] = useState(null);
    const [dashboardFilter, setDashboardFilter] = useState(null);

    // ── Service schedule state ───────────────────────────────────────────────
    // `scheduleOverdueMap` — { [assetId]: { isOverdue, isDueSoon } } lookup built
    //   from a bulk POST to /service-schedules/overdue-summary after each asset
    //   list load. Used to colour-code rows without a per-row API call.
    // `serviceTypes` — available service types (e.g. "Electrical", "HVAC") used
    //   as the select list inside the Add/Edit Schedule modal.
    // `assetSchedules` — schedules for the currently viewed asset (loaded on
    //   demand when the Schedules tab is activated).
    // `schedulesLoading` — spinner flag for the schedules tab.
    // `showScheduleModal` — Add/Edit Service Schedule dialog.
    // `scheduleForm` — form state for that dialog.
    // `scheduleModalSaving` — disables the Save button while the POST/PUT is in-flight.
    // `editingScheduleId` — null = creating new; non-null = editing existing.
    const [scheduleOverdueMap, setScheduleOverdueMap] = useState({});
    const [serviceTypes, setServiceTypes] = useState([]);
    const [assetSchedules, setAssetSchedules] = useState([]);
    const [schedulesLoading, setSchedulesLoading] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [scheduleForm, setScheduleForm] = useState(getEmptyScheduleForm());
    const [scheduleModalSaving, setScheduleModalSaving] = useState(false);
    const [editingScheduleId, setEditingScheduleId] = useState(null);

    // ── Pagination state ─────────────────────────────────────────────────────
    // Server-side pagination. `pageSize` is fixed at 50 and intentionally not
    // exposed as state (no setter) because we don't want to re-render when it
    // never changes. `totalCount` is returned by the server so we can display
    // "Page N of M" and enable/disable navigation buttons accurately.
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(50);
    const [totalCount, setTotalCount] = useState(0);

    // ── Sidebar and DnD state ────────────────────────────────────────────────
    // `sidebarOpen` — toggles the groups sidebar panel.
    // `expandedGroupId` — which group card is expanded in the sidebar.
    // `activeDragId` — the dnd-kit drag ID string of the row being dragged
    //   (e.g. "asset-42"). Used to render the DragOverlay preview card and to
    //   apply visual feedback on the droppable group cards.
    // `quickServiceAssetId` — the assetId for which the inline quick-service
    //   form is open; null when no quick-service form is visible.
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [expandedGroupId, setExpandedGroupId] = useState(null);
    const [activeDragId, setActiveDragId] = useState(null);
    const [quickServiceAssetId, setQuickServiceAssetId] = useState(null);

    // ── DnD sensors ──────────────────────────────────────────────────────────
    // We require a minimum 8px pointer movement before a drag begins. This
    // prevents a slow click from accidentally initiating a drag operation,
    // which would block the onClick handler on the action buttons in the row.
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
    );

    // ── Data Loading ─────────────────────────────────────────────────────────

    /**
     * Fetches all assets with current filters applied.
     *
     * WHY useCallback?
     *   This function is listed as a dependency of the mount useEffect and of
     *   the SignalR callback ref update effect. If it were re-created on every
     *   render the effects would re-run in an infinite loop. useCallback ensures
     *   the identity only changes when one of its declared deps changes.
     *
     * Filtering is split across client and server:
     *   - Server: propertyId, categoryId, status, showInactive, page, pageSize
     *     (these reduce the dataset before it reaches the browser).
     *   - Client: searchQuery (see `filteredAssets`) — applied instantly without
     *     a network round-trip, giving the illusion of real-time filtering.
     *
     * The API returns either a paged envelope { items, totalCount } or a plain
     * array for backwards compatibility; both forms are handled.
     */
    const fetchAssets = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams();
            if (filterPropertyId) params.append('propertyId', filterPropertyId);
            if (filterCategoryId) params.append('categoryId', filterCategoryId);
            if (filterStatus) params.append('status', filterStatus);
            if (showInactive) params.append('includeInactive', 'true');
            params.append('page', currentPage.toString());
            params.append('pageSize', pageSize.toString());

            const queryString = params.toString();
            const url = `/api/AssetManagement/assets${queryString ? `?${queryString}` : ''}`;
            const response = await apiFetch(url);
            const data = await response.json();

            if (data.success) {
                const loadedAssets = data.data?.items || data.data || [];
                setAssets(loadedAssets);
                setTotalCount(data.data?.totalCount ?? (loadedAssets.length || 0));
            } else {
                setError(data.message || 'Failed to load assets');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            setError('Failed to load assets. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [apiFetch, filterPropertyId, filterCategoryId, filterStatus, showInactive, currentPage, pageSize]);

    /**
     * Fetches asset categories for filter dropdowns and form selection.
     *
     * Categories are returned as a tree: each category has a `subCategories`
     * array.  They are sorted alphabetically here so the server can return them
     * in any order without impacting the UI.
     *
     * Failures are swallowed because the page is still functional without
     * category data (the dropdowns simply remain empty).
     */
    const fetchCategories = useCallback(async () => {
        try {
            const response = await apiFetch('/api/AssetManagement/categories');
            const data = await response.json();
            if (data.success) {
                setCategories([...( data.data || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            // Categories are optional for the listing; don't block on failure
        }
    }, [apiFetch]);

    /**
     * Fetches reference data for dropdowns (statuses, interval units, etc.).
     *
     * Two requests are made sequentially inside the same callback:
     *   1. /reference-data — returns { statuses, serviceIntervalUnits,
     *      documentTypes, serviceRecordTypes }.  All form selects pull from
     *      this object rather than hard-coding option lists, so adding a new
     *      enum value on the server automatically appears in the UI.
     *   2. /workflow-rules/reference-data — returns { users } used to populate
     *      the Asset Owner and Asset Manager dropdowns.  Piggybacking on this
     *      callback avoids a separate useCallback/useEffect just for users.
     *
     * Both failures are non-fatal and silently logged to Sentry.
     */
    const fetchReferenceData = useCallback(async () => {
        try {
            const response = await apiFetch('/api/AssetManagement/reference-data');
            const data = await response.json();
            if (data.success) {
                setReferenceData(data.data);
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            // Reference data is optional; don't block on failure
        }
        try {
            const res = await apiFetch('/api/AssetManagement/workflow-rules/reference-data');
            const d = await res.json();
            if (d.success) setUsersList(d.data?.users ?? []);
        } catch {
            // non-critical
        }
    }, [apiFetch]);

    /**
     * Fetches properties the current user has access to for the filter dropdown.
     *
     * The endpoint is protected by property-level access control on the server
     * (PropertyAccessControllerBase), so this list is already scoped to the
     * current user's permissions — no client-side filtering is needed.
     *
     * The response can be wrapped ({ success, data: [] }) or a plain array;
     * both shapes are handled for API version compatibility.
     *
     * Properties are sorted alphabetically by propertyName so the dropdown
     * is consistent regardless of insertion order in the database.
     */
    const fetchProperties = useCallback(async () => {
        try {
            const response = await apiFetch('/api/AssetManagement/properties');
            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                setProperties([...data.data].sort((a, b) => (a.propertyName || '').localeCompare(b.propertyName || '')));
            } else if (Array.isArray(data)) {
                setProperties([...data].sort((a, b) => (a.propertyName || '').localeCompare(b.propertyName || '')));
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            // Properties are optional for filters
        }
    }, [apiFetch]);

    /**
     * Fetches overdue/due-soon summary for currently displayed assets.
     *
     * Rather than embedding schedule status in every asset row returned by
     * the main asset list endpoint (expensive join), we POST the IDs of the
     * currently displayed page to a dedicated summary endpoint.  The server
     * evaluates schedules in bulk and returns { assetId, isOverdue, isDueSoon }
     * for each.  We build a keyed map (scheduleOverdueMap) so DraggableAssetRow
     * can do an O(1) lookup via scheduleOverdueMap[asset.assetId] to decide
     * whether to apply warning row colours — no per-row API calls needed.
     *
     * Called automatically whenever `assets` changes (see useEffect below).
     * Failures are silently ignored because schedule status is supplementary.
     */
    const fetchScheduleOverdueSummary = useCallback(async (assetList) => {
        if (!assetList || assetList.length === 0) return;
        try {
            const ids = assetList.map(a => a.assetId);
            const res = await apiFetch('/api/AssetManagement/service-schedules/overdue-summary', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(ids)
            });
            if (res.ok) {
                const data = await res.json();
                const map = {};
                (data.data || []).forEach(s => { map[s.assetId] = s; });
                setScheduleOverdueMap(map);
            }
        } catch {
            // Non-blocking
        }
    }, [apiFetch]);

    /**
     * Fetches all asset service types for the Add/Edit Service Schedule modal.
     *
     * Service types (e.g. "Electrical Inspection", "HVAC Service") are managed
     * in the Asset Settings page.  Loaded once on mount and kept in component
     * state for the lifetime of the page.
     */
    const fetchServiceTypes = useCallback(async () => {
        try {
            const res = await apiFetch('/api/AssetManagement/service-types');
            if (res.ok) {
                const data = await res.json();
                setServiceTypes(data.data || []);
            }
        } catch { /* non-blocking */ }
    }, [apiFetch]);

    /**
     * Fetches service schedules for the selected asset.
     *
     * Loaded on-demand when the user opens the "Service Schedules" tab inside
     * the asset detail modal.  Each schedule includes pre-computed fields from
     * the server: nextDueDate, daysUntilDue, isOverdue — so the table can
     * display status badges without any client-side date arithmetic.
     *
     * @param {number} assetId - The asset whose schedules should be loaded
     */
    const fetchAssetSchedules = useCallback(async (assetId) => {
        setSchedulesLoading(true);
        try {
            const res = await apiFetch(`/api/AssetManagement/service-schedules?assetId=${assetId}`);
            if (res.ok) {
                const data = await res.json();
                setAssetSchedules(data.data || []);
            }
        } catch (err) {
            Sentry.captureException(err);
        } finally {
            setSchedulesLoading(false);
        }
    }, [apiFetch]);

    // ── Mount effect: kick off all initial data loads in parallel ───────────
    // Each fetch is independent so we call them all inside a single effect
    // rather than separate effects to avoid unnecessary sequential waterfalls.
    // The eslint-disable comment suppresses the react-hooks/set-state-in-effect
    // rule which fires a false positive when setState is called inside a callback
    // that is itself invoked inside useEffect.
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        fetchAssets();
        fetchCategories();
        fetchReferenceData();
        fetchProperties();
        fetchServiceTypes();
    }, [fetchAssets, fetchCategories, fetchReferenceData, fetchProperties, fetchServiceTypes]);

    // ── Overdue summary effect ───────────────────────────────────────────────
    // Runs after every `assets` state update (i.e. after every successful page
    // load).  We only call the API when there is at least one asset to check.
    useEffect(() => {
        if (assets.length > 0) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            fetchScheduleOverdueSummary(assets);
        }
    }, [assets, fetchScheduleOverdueSummary]);

    // ── MDA Tenant Search ────────────────────────────────────────────────────

    /**
     * Searches MDA tenants filtered by the currently selected property.
     * Uses debounced input to avoid excessive API calls.
     */
    const searchTenants = useCallback(async (searchText) => {
        if (!formData.propertyId || !searchText || searchText.length < 2) {
            setTenantSearchResults([]);
            return;
        }
        try {
            setTenantSearchLoading(true);
            const response = await apiFetch(`/api/AssetManagement/mda/tenants?propertyId=${formData.propertyId}&search=${encodeURIComponent(searchText)}`);
            const data = await response.json();
            if (data.success) {
                setTenantSearchResults(data.data || []);
                setShowTenantDropdown(true);
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            setTenantSearchResults([]);
        } finally {
            setTenantSearchLoading(false);
        }
    }, [apiFetch, formData.propertyId]);

    // ── Debounced tenant search ───────────────────────────────────────────
    // `tenantSearchTimerRef` stores the setTimeout handle so we can cancel a
    // pending search if the user keeps typing before the 300 ms elapses.
    // The same pattern is used for rentable-unit search (rentableUnitTimerRef).
    // We clear mdaTenantId immediately on each keystroke so the linked badge
    // disappears as soon as the user modifies the field, making it obvious the
    // link is broken until a new selection is made.
    const tenantSearchTimerRef = useRef(null);
    const handleTenantSearchChange = (value) => {
        setFormData(prev => ({ ...prev, tenantName: value, mdaTenantId: null }));
        if (tenantSearchTimerRef.current) clearTimeout(tenantSearchTimerRef.current);
        tenantSearchTimerRef.current = setTimeout(() => searchTenants(value), 300);
    };

    /**
     * Selects a tenant from the autocomplete dropdown.
     *
     * Auto-link pattern: once a tenant is chosen we immediately call
     * /mda/unit-for-tenant to find the rentable unit they occupy.  If found,
     * the unit fields are pre-filled automatically.  This is best-effort—
     * failures are silently ignored so the user can still save without a unit.
     *
     * @param {Object} tenant - The MDA tenant object from the search results
     */
    const selectTenant = (tenant) => {
        setFormData(prev => ({
            ...prev,
            tenantName: tenant.listOrTradingAsName || tenant.lesseeName,
            mdaTenantId: tenant.tenantID
        }));
        setShowTenantDropdown(false);
        setTenantSearchResults([]);

        // Auto-link: fetch the rentable unit associated with this tenant
        if (formData.propertyId && tenant.tenantID) {
            apiFetch(`/api/AssetManagement/mda/unit-for-tenant?propertyId=${formData.propertyId}&tenantId=${tenant.tenantID}`)
                .then(res => res.json())
                .then(data => {
                    if (data.success && data.data) {
                        const unitLabel = data.data.propertyRentableUnitNo + (data.data.description ? ` - ${data.data.description}` : '');
                        setFormData(prev => ({
                            ...prev,
                            mdaRentableUnitId: data.data.propertyRentableUnitID,
                            rentableUnitName: unitLabel
                        }));
                        setRentableUnitSearch(unitLabel);
                    }
                })
                .catch(() => { /* auto-link is best-effort */ });
        }
    };

    // Close tenant dropdown when the user clicks anywhere outside both the
    // search input and the dropdown list.  Both refs are checked because a
    // click on the input itself should not close the dropdown — only a click
    // on the rest of the page should.
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (tenantDropdownRef.current && !tenantDropdownRef.current.contains(e.target)
                && tenantSearchRef.current && !tenantSearchRef.current.contains(e.target)) {
                setShowTenantDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ── MDA Rentable Unit Search ─────────────────────────────────────────────

    /**
     * Fetches MDA rentable units filtered by the currently selected property.
     */
    const fetchRentableUnits = useCallback(async (searchText) => {
        if (!formData.propertyId) {
            setRentableUnitResults([]);
            return;
        }
        try {
            setRentableUnitLoading(true);
            const params = new URLSearchParams({ propertyId: formData.propertyId });
            if (searchText) params.append('search', searchText);
            const response = await apiFetch(`/api/AssetManagement/mda/rentable-units?${params}`);
            const data = await response.json();
            if (data.success) {
                setRentableUnitResults(data.data || []);
                setShowRentableUnitDropdown(true);
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            setRentableUnitResults([]);
        } finally {
            setRentableUnitLoading(false);
        }
    }, [apiFetch, formData.propertyId]);

    // Debounced rentable-unit search — same 300 ms delay pattern as tenant search.
    const rentableUnitTimerRef = useRef(null);
    const handleRentableUnitSearchChange = (value) => {
        setRentableUnitSearch(value);
        setFormData(prev => ({ ...prev, mdaRentableUnitId: null, rentableUnitName: '' }));
        if (rentableUnitTimerRef.current) clearTimeout(rentableUnitTimerRef.current);
        rentableUnitTimerRef.current = setTimeout(() => fetchRentableUnits(value), 300);
    };

    /**
     * Selects a rentable unit from the autocomplete dropdown.
     *
     * Auto-link pattern (inverse of selectTenant): once a unit is chosen we
     * call /mda/tenant-for-unit to find the current occupant.  If found, the
     * tenant fields and belongsToTenant flag are pre-filled automatically.
     * This is best-effort — failures are silently ignored.
     *
     * @param {Object} unit - The MDA rentable unit object from the search results
     */
    const selectRentableUnit = (unit) => {
        setFormData(prev => ({
            ...prev,
            mdaRentableUnitId: unit.propertyRentableUnitID,
            rentableUnitName: unit.propertyRentableUnitNo + (unit.description ? ` - ${unit.description}` : '')
        }));
        setRentableUnitSearch(unit.propertyRentableUnitNo + (unit.description ? ` - ${unit.description}` : ''));
        setShowRentableUnitDropdown(false);

        // Auto-link: fetch the tenant associated with this rentable unit
        if (formData.propertyId && unit.propertyRentableUnitID) {
            apiFetch(`/api/AssetManagement/mda/tenant-for-unit?propertyId=${formData.propertyId}&rentableUnitId=${unit.propertyRentableUnitID}`)
                .then(res => res.json())
                .then(data => {
                    if (data.success && data.data) {
                        setFormData(prev => ({
                            ...prev,
                            tenantName: data.data.listOrTradingAsName || data.data.lesseeName,
                            mdaTenantId: data.data.tenantID,
                            belongsToTenant: true
                        }));
                    }
                })
                .catch(() => { /* auto-link is best-effort */ });
        }
    };

    // Close rentable unit dropdown on outside click.
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (rentableUnitDropdownRef.current && !rentableUnitDropdownRef.current.contains(e.target)) {
                setShowRentableUnitDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // When the user changes the property filter, any previously loaded tenant
    // and rentable unit search results become invalid (they belong to the old
    // property).  Reset them all so stale data is not shown in the dropdowns.
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setTenantSearchResults([]);
        setRentableUnitResults([]);
        setShowTenantDropdown(false);
        setShowRentableUnitDropdown(false);
    }, [formData.propertyId]);

    // ── Asset CRUD ───────────────────────────────────────────────────────────

    /**
     * Opens the create asset modal with empty form data.
     */
    const handleCreateNew = () => {
        setFormData(getEmptyFormData());
        setRentableUnitSearch('');
        setShowCreateModal(true);
    };

    /**
     * Opens the edit modal for an existing asset.
     * @param {Object} asset - The asset to edit
     */
    const handleEdit = (asset) => {
        setFormData({
            assetId: asset.assetId,
            name: asset.name || '',
            assetTag: asset.assetTag || '',
            serialNumber: asset.serialNumber || '',
            manufacturer: asset.manufacturer || '',
            model: asset.model || '',
            description: asset.description || '',
            propertyId: asset.propertyId || '',
            belongsToTenant: asset.belongsToTenant || false,
            tenantName: asset.tenantName || '',
            mdaTenantId: asset.mdaTenantId || null,
            location: asset.location || '',
            mdaRentableUnitId: asset.mdaRentableUnitId || null,
            rentableUnitName: asset.rentableUnitName || '',
            assetCategoryId: asset.assetCategoryId || '',
            assetSubCategoryId: asset.assetSubCategoryId || '',
            isNew: asset.isNew || false,
            installationDate: asset.installationDate ? asset.installationDate.substring(0, 10) : '',
            commissioningDate: asset.commissioningDate ? asset.commissioningDate.substring(0, 10) : '',
            requiresServicing: asset.requiresServicing || false,
            serviceIntervalValue: asset.serviceIntervalValue || '',
            serviceIntervalUnit: asset.serviceIntervalUnit || '',
            lastServiceDate: asset.lastServiceDate ? asset.lastServiceDate.substring(0, 10) : '',
            hasWarranty: asset.hasWarranty || false,
            warrantyExpiryDate: asset.warrantyExpiryDate ? asset.warrantyExpiryDate.substring(0, 10) : '',
            warrantyNotes: asset.warrantyNotes || '',
            purchaseCost: asset.purchaseCost || '',
            purchaseCostIncludesVat: asset.purchaseCostIncludesVat || false,
            quantity: asset.quantity || 1,
            notes: asset.notes || '',
            assetOwnerId: asset.assetOwnerId ?? '',
            assetManagerId: asset.assetManagerId ?? ''
        });
        setRentableUnitSearch(asset.rentableUnitName || '');
        setShowCreateModal(true);
    };

    /**
     * Saves the asset (create or update) based on whether assetId is present.
     *
     * The payload is constructed by spreading formData and then selectively
     * overriding the string values that the HTML form elements produce with the
     * types the server expects (integers for IDs, floats for monetary values,
     * null for empty optional fields instead of empty strings).
     *
     * WHY spread + override rather than a hand-built object?
     *   New fields added to getEmptyFormData() are automatically included in
     *   the payload without needing to update this function, reducing the
     *   chance of a field being silently omitted.
     *
     * After a successful save both the asset list and dashboard stats are
     * refreshed so the summary cards reflect the new counts immediately.
     */
    const handleSaveAsset = async () => {
        if (!formData.name.trim()) {
            toast.warning('Asset name is required');
            return;
        }
        if (!formData.propertyId) {
            toast.warning('Property is required');
            return;
        }
        if (!formData.assetCategoryId) {
            toast.warning('Asset category is required');
            return;
        }

        try {
            setSaving(true);

            const payload = {
                ...formData,
                propertyId: parseInt(formData.propertyId),
                assetCategoryId: parseInt(formData.assetCategoryId),
                assetSubCategoryId: formData.assetSubCategoryId ? parseInt(formData.assetSubCategoryId) : null,
                serviceIntervalValue: formData.serviceIntervalValue ? parseInt(formData.serviceIntervalValue) : null,
                purchaseCost: formData.purchaseCost ? parseFloat(formData.purchaseCost) : null,
                purchaseCostIncludesVat: formData.purchaseCostIncludesVat,
                quantity: formData.quantity ? parseInt(formData.quantity) : 1,
                installationDate: formData.installationDate || null,
                commissioningDate: formData.commissioningDate || null,
                lastServiceDate: formData.lastServiceDate || null,
                warrantyExpiryDate: formData.warrantyExpiryDate || null,
                assetOwnerId: formData.assetOwnerId !== '' ? parseInt(formData.assetOwnerId) : null,
                assetManagerId: formData.assetManagerId !== '' ? parseInt(formData.assetManagerId) : null
            };

            const isUpdate = !!formData.assetId;
            const url = isUpdate
                ? `/api/AssetManagement/assets/${formData.assetId}`
                : '/api/AssetManagement/assets';
            const method = isUpdate ? 'PUT' : 'POST';

            const response = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();

            if (data.success) {
                setShowCreateModal(false);
                toast.success(formData.assetId ? 'Asset updated successfully' : 'Asset created successfully');
                fetchAssets();
                fetchDashboardStats();
            } else {
                toast.error(data.message || 'Failed to save asset');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to save asset. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    /**
     * Toggles the active status of an asset (soft delete/restore).
     *
     * A native browser confirm() dialog is shown before the PATCH so the user
     * has a chance to cancel.  We use a PATCH because only the isActive flag
     * changes — sending a full PUT body would be wasteful and error-prone.
     *
     * Both the asset list and dashboard stats are refreshed on success so the
     * "Total Active Assets" card updates immediately.
     *
     * @param {Object} asset - The asset to toggle
     */
    const handleToggleActive = async (asset) => {
        const confirmMessage = asset.isActive
            ? `Deactivate asset "${asset.name}"?`
            : `Reactivate asset "${asset.name}"?`;
        if (!window.confirm(confirmMessage)) return;

        try {
            const response = await apiFetch(`/api/AssetManagement/assets/${asset.assetId}/toggle-active`, {
                method: 'PATCH'
            });
            const data = await response.json();
            if (data.success) {
                toast.success(asset.isActive ? 'Asset deactivated' : 'Asset reactivated');
                fetchAssets();
                fetchDashboardStats();
            } else {
                toast.error(data.message || 'Failed to update asset');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to update asset. Please try again.');
        }
    };

    /**
     * Opens the asset detail view modal.
     *
     * The listing endpoint returns a lightweight asset DTO (no service records,
     * documents, or audit logs) to keep the table fast.  This function loads
     * the full asset DTO from GET /assets/{id} which includes serviceRecords,
     * auditLogs, and documents arrays used by the detail modal tabs.
     *
     * Also called internally after workflow actions (submit/approve/reject) to
     * refresh the detail view with the latest approval statuses without closing
     * the modal.
     *
     * @param {Object} asset - The asset to view (only assetId is required)
     */
    const handleViewDetail = async (asset) => {
        try {
            const response = await apiFetch(`/api/AssetManagement/assets/${asset.assetId}`);
            const data = await response.json();
            if (data.success) {
                setSelectedAsset(data.data);
                setDetailTab('details');
                setShowDetailModal(true);
            } else {
                toast.error(data.message || 'Failed to load asset details');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to load asset details. Please try again.');
        }
    };

    // ── Service Record Operations ────────────────────────────────────────────

    /**
     * Opens the create service record modal for the currently viewed asset.
     */
    const handleCreateServiceRecord = () => {
        setServiceFormData(getEmptyServiceForm());
        setShowServiceModal(true);
    };

    /**
     * Saves a new service record for the currently selected asset.
     */
    const handleSaveServiceRecord = async () => {
        if (!serviceFormData.title.trim()) {
            toast.warning('Service record title is required');
            return;
        }

        try {
            setSaving(true);

            const payload = {
                assetId: selectedAsset.assetId,
                recordType: serviceFormData.recordType,
                title: serviceFormData.title,
                description: serviceFormData.description,
                serviceDueDate: serviceFormData.serviceDueDate || null,
                serviceDate: serviceFormData.serviceDate || null,
                serviceProviderName: serviceFormData.serviceProviderName,
                providerReference: serviceFormData.providerReference,
                cost: serviceFormData.cost ? parseFloat(serviceFormData.cost) : null,
                costIncludesVat: serviceFormData.costIncludesVat,
                costNotes: serviceFormData.costNotes,
                notes: serviceFormData.notes
            };

            const response = await apiFetch('/api/AssetManagement/service-records', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();

            if (data.success) {
                setShowServiceModal(false);
                toast.success('Service record created successfully');
                // Refresh the detail view
                handleViewDetail(selectedAsset);
            } else {
                toast.error(data.message || 'Failed to create service record');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to create service record. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // ── Document Management ──────────────────────────────────────────────────

    /**
     * Handles file selection and uploads a document to the asset.
     * Uses FormData for multipart upload to support file content.
     */
    const handleDocumentUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file || !selectedAsset) return;

        try {
            setUploadingDocument(true);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('documentType', documentType);
            formData.append('notes', documentNotes);

            const response = await apiFetch(
                `/api/AssetManagement/assets/${selectedAsset.assetId}/documents/upload`,
                { method: 'POST', body: formData }
            );
            const data = await response.json();

            if (data.success) {
                setDocumentNotes('');
                toast.success('Document uploaded successfully');
                // Refresh the detail view to show the new document
                handleViewDetail(selectedAsset);
            } else {
                toast.error(data.message || 'Failed to upload document');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to upload document. Please try again.');
        } finally {
            setUploadingDocument(false);
            // Clear the file input so the same file can be re-selected
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    /**
     * Downloads a document from the server via the asset document endpoint.
     *
     * The download is triggered by programmatically creating and clicking a
     * hidden anchor element.  This is the standard browser technique for
     * initiating a file download from a Blob without navigating away from
     * the current page.  The object URL is revoked immediately after the
     * click to free the associated memory.
     *
     * @param {number} documentId - Server-assigned document ID
     * @param {string} fileName - Suggested file name for the download dialog
     */
    const handleDocumentDownload = async (documentId, fileName) => {
        try {
            const response = await apiFetch(
                `/api/AssetManagement/assets/${selectedAsset.assetId}/documents/${documentId}/download`
            );
            if (!response.ok) {
                toast.error('Failed to download document');
                return;
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = fileName || 'document';
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            window.URL.revokeObjectURL(url);
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to download document. Please try again.');
        }
    };

    /**
     * Soft-deletes a document from the asset after user confirmation.
     *
     * The server performs a soft delete (marks isDeleted = true) rather than
     * permanently removing the file, preserving the audit trail.
     *
     * @param {number} documentId - Server-assigned document ID to delete
     */
    const handleDocumentDelete = async (documentId) => {
        if (!window.confirm('Are you sure you want to delete this document?')) return;

        try {
            const response = await apiFetch(
                `/api/AssetManagement/assets/${selectedAsset.assetId}/documents/${documentId}`,
                { method: 'DELETE' }
            );
            const data = await response.json();

            if (data.success) {
                toast.success('Document deleted');
                handleViewDetail(selectedAsset);
            } else {
                toast.error(data.message || 'Failed to delete document');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to delete document. Please try again.');
        }
    };

    // ── Asset Group Operations ───────────────────────────────────────────────

    /**
     * Fetches asset groups for the currently selected property filter.
     */
    const fetchGroups = useCallback(async (propertyId) => {
        if (!propertyId) {
            setGroups([]);
            return;
        }
        try {
            const response = await apiFetch(`/api/AssetManagement/groups?propertyId=${propertyId}`);
            const data = await response.json();
            if (data.success) {
                setGroups(data.data || []);
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            // Groups are optional
        }
    }, [apiFetch]);

    /**
     * Opens the create group modal.
     *
     * Pre-fills `propertyId` from the current filter so the user does not have
     * to re-select it when a property is already active in the listing view.
     */
    const handleCreateGroup = () => {
        setGroupFormData({
            ...getEmptyGroupForm(),
            propertyId: filterPropertyId
        });
        setShowGroupModal(true);
    };

    /**
     * Opens the edit group modal for an existing group.
     *
     * @param {Object} group - The group to edit
     */
    const handleEditGroup = (group) => {
        setGroupFormData({
            assetGroupId: group.assetGroupId,
            name: group.name || '',
            description: group.description || '',
            propertyId: group.propertyId,
            assetCategoryId: group.assetCategoryId || '',
            quantity: group.quantity || '',
            notes: group.notes || ''
        });
        setShowGroupModal(true);
    };

    /**
     * Saves a group (create or update).
     *
     * Follows the same create-or-update pattern as handleSaveAsset: the URL
     * and HTTP method are chosen based on whether assetGroupId is set.
     */
    const handleSaveGroup = async () => {
        if (!groupFormData.name.trim()) {
            toast.warning('Group name is required');
            return;
        }
        if (!groupFormData.propertyId) {
            toast.warning('Property is required');
            return;
        }

        try {
            setSaving(true);
            const payload = {
                name: groupFormData.name,
                description: groupFormData.description,
                propertyId: parseInt(groupFormData.propertyId),
                assetCategoryId: groupFormData.assetCategoryId ? parseInt(groupFormData.assetCategoryId) : null,
                quantity: groupFormData.quantity ? parseInt(groupFormData.quantity) : null,
                notes: groupFormData.notes
            };

            const isUpdate = !!groupFormData.assetGroupId;
            const url = isUpdate
                ? `/api/AssetManagement/groups/${groupFormData.assetGroupId}`
                : '/api/AssetManagement/groups';
            const method = isUpdate ? 'PUT' : 'POST';

            const response = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();

            if (data.success) {
                setShowGroupModal(false);
                toast.success(groupFormData.assetGroupId ? 'Group updated' : 'Group created');
                fetchGroups(filterPropertyId);
            } else {
                toast.error(data.message || 'Failed to save group');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to save group. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    /**
     * Loads the full group detail (with members list) from the server.
     *
     * The group list card only contains summary data (name, memberCount, etc.).
     * When the user expands a group card in the sidebar, we load the full
     * group object which includes the `members` array needed to render the
     * member chips and the bulk service member selection table.
     *
     * @param {Object} group - Must have at least { assetGroupId }
     */
    const handleViewGroup = async (group) => {
        try {
            const response = await apiFetch(`/api/AssetManagement/groups/${group.assetGroupId}`);
            const data = await response.json();
            if (data.success) {
                setSelectedGroup(data.data);
            } else {
                toast.error(data.message || 'Failed to load group details');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to load group details. Please try again.');
        }
    };

    /**
     * Opens the bulk service record modal for the selected group.
     *
     * Resets both the bulk form data and the excluded-assets set so that a
     * fresh service event is started each time.
     *
     * @param {Object} group - The group to create a bulk service event for
     */
    const handleCreateBulkService = (group) => {
        setSelectedGroup(group);
        setBulkServiceFormData(getEmptyBulkServiceForm());
        setExcludedAssetIds([]);
        setShowBulkServiceModal(true);
    };

    /**
     * Toggles an asset's exclusion from a bulk service operation.
     *
     * Uses functional state update with an inclusion/exclusion toggle so no
     * stale-closure issue occurs if state updates are batched.
     *
     * @param {number} assetId - The asset to include or exclude
     */
    const toggleExcludeAsset = (assetId) => {
        setExcludedAssetIds(prev =>
            prev.includes(assetId)
                ? prev.filter(id => id !== assetId)
                : [...prev, assetId]
        );
    };

    /**
     * Saves a bulk service record for the selected group.
     *
     * The server creates one service record per included member and returns
     * { recordsCreated, excludedCount } in the response body.  We display both
     * numbers in the success toast so the user knows the exact outcome.
     *
     * excludedAssetIds is sent as null rather than [] when empty to signal
     * "include all" explicitly on the server side.
     */
    const handleSaveBulkServiceRecord = async () => {
        if (!bulkServiceFormData.title.trim()) {
            toast.warning('Service record title is required');
            return;
        }
        if (!selectedGroup) return;

        try {
            setSaving(true);
            const payload = {
                assetGroupId: selectedGroup.assetGroupId,
                recordType: bulkServiceFormData.recordType,
                title: bulkServiceFormData.title,
                description: bulkServiceFormData.description,
                serviceDueDate: bulkServiceFormData.serviceDueDate || null,
                serviceDate: bulkServiceFormData.serviceDate || null,
                serviceProviderName: bulkServiceFormData.serviceProviderName,
                providerReference: bulkServiceFormData.providerReference,
                cost: bulkServiceFormData.cost ? parseFloat(bulkServiceFormData.cost) : null,
                costIncludesVat: bulkServiceFormData.costIncludesVat,
                costNotes: bulkServiceFormData.costNotes,
                notes: bulkServiceFormData.notes,
                excludedAssetIds: excludedAssetIds.length > 0 ? excludedAssetIds : null
            };

            const response = await apiFetch('/api/AssetManagement/service-records/bulk', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();

            if (data.success) {
                setShowBulkServiceModal(false);
                toast.success(`Bulk service record created — ${data.data.recordsCreated} asset(s) serviced, ${data.data.excludedCount} excluded`);
                fetchGroups(filterPropertyId);
            } else {
                toast.error(data.message || 'Failed to create bulk service record');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to create bulk service record. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    /**
     * Adds one or more assets to a group.
     *
     * Called from two places:
     *   1. handleDragEnd — when an asset row is dropped onto a group card in the sidebar.
     *   2. handleAddSelectedToGroup — when the user uses the multi-select toolbar.
     *
     * If the user currently has the target group expanded in the sidebar,
     * we also reload the group detail so the new member appears in the list.
     *
     * @param {number} groupId - The destination group
     * @param {number[]} assetIds - The assets to add
     */
    const handleAddAssetsToGroup = async (groupId, assetIds) => {
        try {
            const response = await apiFetch(`/api/AssetManagement/groups/${groupId}/members`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ assetIds })
            });
            const data = await response.json();
            if (data.success) {
                toast.success(data.message);
                fetchGroups(filterPropertyId);
                if (selectedGroup?.assetGroupId === groupId) handleViewGroup({ assetGroupId: groupId });
            } else {
                toast.error(data.message || 'Failed to add assets to group');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to add assets to group. Please try again.');
        }
    };

    /**
     * Removes one or more assets from a group.
     *
     * Note: this does not delete the asset — it only removes the group
     * membership record (AssetGroupMember row).  The asset continues to exist
     * and can be added to another group.
     *
     * @param {number} groupId - The group to remove from
     * @param {number[]} assetIds - The assets to remove
     */
    const handleRemoveGroupMembers = async (groupId, assetIds) => {
        if (!window.confirm('Remove selected asset(s) from this group?')) return;
        try {
            const response = await apiFetch(`/api/AssetManagement/groups/${groupId}/members`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ assetIds })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Asset(s) removed from group');
                if (selectedGroup?.assetGroupId === groupId) handleViewGroup({ assetGroupId: groupId });
                fetchGroups(filterPropertyId);
            } else {
                toast.error(data.message || 'Failed to remove assets from group');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to remove assets from group. Please try again.');
        }
    };

    // Reload groups whenever the property filter changes.
    // The eslint-disable comment suppresses the false-positive for setState
    // being called inside a useCallback inside useEffect.
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        fetchGroups(filterPropertyId);
    }, [filterPropertyId, fetchGroups]);

    // Reset to page 1 whenever any server-side filter changes so we never
    // show an out-of-range page (e.g. currently on page 5 but the new filter
    // only has 2 pages of results).
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setCurrentPage(1);
    }, [filterPropertyId, filterCategoryId, filterStatus, showInactive]);

    // Clear multi-select when filters or pagination change.  A selection made
    // on page 1 is meaningless on page 2, and stale IDs from a previous filter
    // set could accidentally end up in a group-add operation.
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedAssetIds([]);
    }, [filterPropertyId, filterCategoryId, filterStatus, showInactive, currentPage]);

    // ── Dashboard Stats ──────────────────────────────────────────────────────

    const fetchDashboardStats = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (filterPropertyId) params.append('propertyId', filterPropertyId);
            const qs = params.toString();
            const response = await apiFetch(`/api/AssetManagement/dashboard${qs ? `?${qs}` : ''}`);
            const data = await response.json();
            if (data.success) setDashboardStats(data.data);
        } catch (catchError) {
            Sentry.captureException(catchError);
            // Dashboard stats are supplementary — don't block on failure
        }
    }, [apiFetch, filterPropertyId]);

    // Trigger a dashboard stats refresh whenever the property filter changes
    // (filterPropertyId is a dep of the fetchDashboardStats useCallback).
    useEffect(() => {
        fetchDashboardStats();
    }, [fetchDashboardStats]);

    // Keep the fetchAssets and fetchDashboardStats refs pointing at the latest
    // function instances.  This runs after every render where either function
    // identity changes (i.e. when their deps change), ensuring SignalR handlers
    // always call the current version.
    useEffect(() => {
        fetchAssetsRef.current = fetchAssets;
        fetchDashboardStatsRef.current = fetchDashboardStats;
    }, [fetchAssets, fetchDashboardStats]);

    // Join the property-scoped SignalR group so we receive real-time change
    // notifications from other users working on the same property.
    // The cleanup function (returned from useEffect) leaves the group when the
    // component unmounts or when the property filter changes to a new value.
    useEffect(() => {
        if (filterPropertyId) {
            joinProperty(filterPropertyId);
        }
        return () => {
            if (filterPropertyId) {
                leaveProperty(filterPropertyId);
            }
        };
    }, [filterPropertyId, joinProperty, leaveProperty]);

    // ── Service Record Workflow ──────────────────────────────────────────────

    /**
     * Submits a service record for Procurement review.
     */
    const handleSubmitServiceRecord = async (recordId) => {
        try {
            setWorkflowLoading(true);
            const response = await apiFetch(`/api/AssetManagement/service-records/${recordId}/submit`, {
                method: 'PATCH'
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Service record submitted for review');
                handleViewDetail(selectedAsset);
            } else {
                toast.error(data.message || 'Failed to submit service record');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to submit service record. Please try again.');
        } finally {
            setWorkflowLoading(false);
        }
    };

    /**
     * Approves a single service record (Submitted → Approved).
     *
     * An empty JSON body {} is required because the server action attribute
     * [FromBody] expects a body even for approval (no additional data needed).
     *
     * After approval the server executes workflow rules (e.g. reassign Action
     * Items, send notifications) via ExecuteWorkflowRulesAsync on OnApproved.
     *
     * @param {number} recordId - The service record to approve
     */
    const handleApproveServiceRecord = async (recordId) => {
        try {
            setWorkflowLoading(true);
            const response = await apiFetch(`/api/AssetManagement/service-records/${recordId}/approve`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({})
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Service record approved');
                handleViewDetail(selectedAsset);
            } else {
                toast.error(data.message || 'Failed to approve service record');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to approve service record. Please try again.');
        } finally {
            setWorkflowLoading(false);
        }
    };

    /**
     * Opens the rejection reason modal for a service record or bulk batch.
     *
     * Both recordId and bulkRef are stored in state so handleConfirmReject can
     * build the correct URL without needing them passed again.
     *
     * @param {number|null} recordId - The individual record to reject (null for bulk-only)
     * @param {string|null} bulkRef - The bulk batch GUID to reject (null for individual)
     */
    const handleOpenRejectModal = (recordId, bulkRef = null) => {
        setRejectTargetId(recordId);
        setRejectTargetBulkRef(bulkRef);
        setRejectionReason('');
        setShowRejectModal(true);
    };

    /**
     * Confirms the rejection of a service record or bulk batch.
     *
     * The URL is chosen based on whether a bulkRef is set.  Both the individual
     * and bulk reject endpoints accept { rejectionReason } in the body.
     * The reason is trimmed before sending to avoid leading/trailing whitespace
     * in the stored rejection note.
     */
    const handleConfirmReject = async () => {
        if (!rejectionReason.trim()) {
            toast.warning('Please provide a reason for rejection');
            return;
        }
        try {
            setWorkflowLoading(true);
            const url = rejectTargetBulkRef
                ? `/api/AssetManagement/service-records/bulk/${rejectTargetBulkRef}/reject`
                : `/api/AssetManagement/service-records/${rejectTargetId}/reject`;
            const response = await apiFetch(url, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rejectionReason: rejectionReason.trim() })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Service record rejected');
                setShowRejectModal(false);
                handleViewDetail(selectedAsset);
            } else {
                toast.error(data.message || 'Failed to reject service record');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to reject service record. Please try again.');
        } finally {
            setWorkflowLoading(false);
        }
    };

    /**
     * Submits all Pending records in a bulk batch (Pending → Submitted).
     *
     * The server returns { submittedCount } which we display in the toast so
     * the user sees how many records were affected.
     *
     * @param {string} bulkRef - The bulk batch GUID
     */
    const handleSubmitBulkRecords = async (bulkRef) => {
        try {
            setWorkflowLoading(true);
            const response = await apiFetch(`/api/AssetManagement/service-records/bulk/${bulkRef}/submit`, {
                method: 'PATCH'
            });
            const data = await response.json();
            if (data.success) {
                toast.success(`${data.data?.submittedCount || ''} record(s) submitted for review`);
                handleViewDetail(selectedAsset);
            } else {
                toast.error(data.message || 'Failed to submit bulk records');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to submit bulk records. Please try again.');
        } finally {
            setWorkflowLoading(false);
        }
    };

    /**
     * Approves all Submitted records in a bulk batch (Submitted → Approved).
     *
     * @param {string} bulkRef - The bulk batch GUID
     */
    const handleApproveBulkRecords = async (bulkRef) => {
        try {
            setWorkflowLoading(true);
            const response = await apiFetch(`/api/AssetManagement/service-records/bulk/${bulkRef}/approve`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({})
            });
            const data = await response.json();
            if (data.success) {
                toast.success(`${data.data?.approvedCount || ''} record(s) approved`);
                handleViewDetail(selectedAsset);
            } else {
                toast.error(data.message || 'Failed to approve bulk records');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to approve bulk records. Please try again.');
        } finally {
            setWorkflowLoading(false);
        }
    };

    // ── Asset Selection ──────────────────────────────────────────────────────

    /**
     * Toggles an individual asset's selection state.
     */
    const toggleAssetSelection = (assetId) => {
        setSelectedAssetIds(prev =>
            prev.includes(assetId)
                ? prev.filter(id => id !== assetId)
                : [...prev, assetId]
        );
    };

    /**
     * Toggles select-all for the currently visible (client-filtered) assets.
     *
     * Compares against `filteredAssets` (not `assets`) so that only the assets
     * the user can actually see are considered, not hidden rows filtered out by
     * the search query or dashboard filter.
     */
    const toggleSelectAll = () => {
        if (selectedAssetIds.length === filteredAssets.length && filteredAssets.length > 0) {
            setSelectedAssetIds([]);
        } else {
            setSelectedAssetIds(filteredAssets.map(a => a.assetId));
        }
    };

    /**
     * Adds all currently selected assets to a group, then clears the selection
     * and closes the group picker dropdown.
     *
     * @param {number} groupId - The target group
     */
    const handleAddSelectedToGroup = async (groupId) => {
        await handleAddAssetsToGroup(groupId, selectedAssetIds);
        setSelectedAssetIds([]);
        setShowGroupPicker(false);
    };

    // ── Pagination ───────────────────────────────────────────────────────────

    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

    // ── Drag-and-Drop Handlers ───────────────────────────────────────────────

    /**
     * Called when a drag starts — tracks the asset ID being dragged.
     */
    const handleDragStart = (event) => {
        setActiveDragId(event.active.id);
    };

    /**
     * Called when a drag ends — adds the asset to the group it was dropped on.
     */
    const handleDragEnd = async (event) => {
        const { active, over } = event;
        setActiveDragId(null);

        if (!over) return;

        // The droppable ID is prefixed with "group-"
        const groupId = parseInt(over.id.toString().replace('group-', ''));
        const assetId = parseInt(active.id.toString().replace('asset-', ''));

        if (isNaN(groupId) || isNaN(assetId)) return;

        await handleAddAssetsToGroup(groupId, [assetId]);
    };

    // ── Quick Service ────────────────────────────────────────────────────────

    /**
     * Opens a quick inline service form for a specific asset.
     */
    const handleQuickService = (asset) => {
        setQuickServiceAssetId(asset.assetId);
        setServiceFormData(getEmptyServiceForm());
    };

    /**
     * Saves a quick service record and closes the inline form.
     *
     * Refreshes both the asset list and dashboard stats on success (rather than
     * the detail modal, since the quick-service form does not open the detail
     * modal — we just want the service status column to update in-place).
     *
     * @param {number} assetId - The asset to create the service record for
     */
    const handleSaveQuickService = async (assetId) => {
        if (!serviceFormData.title.trim()) {
            toast.warning('Service record title is required');
            return;
        }
        try {
            setSaving(true);
            const payload = {
                assetId: assetId,
                recordType: serviceFormData.recordType,
                title: serviceFormData.title,
                description: serviceFormData.description,
                serviceDueDate: serviceFormData.serviceDueDate || null,
                serviceDate: serviceFormData.serviceDate || null,
                serviceProviderName: serviceFormData.serviceProviderName,
                providerReference: serviceFormData.providerReference,
                cost: serviceFormData.cost ? parseFloat(serviceFormData.cost) : null,
                costIncludesVat: serviceFormData.costIncludesVat,
                costNotes: serviceFormData.costNotes,
                notes: serviceFormData.notes
            };
            const response = await apiFetch('/api/AssetManagement/service-records', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.success) {
                setQuickServiceAssetId(null);
                toast.success('Service record created');
                fetchAssets();
                fetchDashboardStats();
            } else {
                toast.error(data.message || 'Failed to create service record');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to create service record. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // ── Dashboard Filter ─────────────────────────────────────────────────────

    /**
     * Toggles a dashboard filter when clicking a summary card.
     */
    const handleDashboardCardClick = (filterName) => {
        setDashboardFilter(prev => prev === filterName ? null : filterName);
    };

    /**
     * Returns the CSS class name and display label for a service record's
     * approval status badge.
     *
     * Centralised here so all places that render a badge (service record cards,
     * future list views) use a consistent mapping.
     *
     * @param {string} status - 'Pending' | 'Submitted' | 'Approved' | 'Rejected'
     * @returns {{ className: string, label: string }}
     */
    const getApprovalBadge = (status) => {
        switch (status) {
            case 'Pending': return { className: 'approval-pending', label: 'Pending' };
            case 'Submitted': return { className: 'approval-submitted', label: 'Submitted' };
            case 'Approved': return { className: 'approval-approved', label: 'Approved' };
            case 'Rejected': return { className: 'approval-rejected', label: 'Rejected' };
            default: return { className: 'approval-pending', label: status };
        }
    };

    // ── Filtering ────────────────────────────────────────────────────────────

    /**
     * Filters assets client-side by search query and dashboard summary filter.
     */
    const filteredAssets = assets.filter(asset => {
        // Dashboard summary card filter
        if (dashboardFilter) {
            switch (dashboardFilter) {
                case 'overdue': {
                    if (!asset.requiresServicing || !asset.nextServiceDueDate) return false;
                    if (new Date(asset.nextServiceDueDate) >= new Date()) return false;
                    break;
                }
                case 'dueSoon': {
                    if (!asset.requiresServicing || !asset.nextServiceDueDate) return false;
                    const dueDate = new Date(asset.nextServiceDueDate);
                    const now = new Date();
                    const days = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
                    if (days < 0 || days > 30) return false;
                    break;
                }
                case 'warrantyExpired': {
                    if (!asset.hasWarranty || !asset.warrantyExpiryDate) return false;
                    if (new Date(asset.warrantyExpiryDate) >= new Date()) return false;
                    break;
                }
                case 'underRepair': {
                    if (!asset.isUnderRepair) return false;
                    break;
                }
                default: break;
            }
        }

        // Search query filter
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
            (asset.name && asset.name.toLowerCase().includes(query)) ||
            (asset.assetTag && asset.assetTag.toLowerCase().includes(query)) ||
            (asset.serialNumber && asset.serialNumber.toLowerCase().includes(query)) ||
            (asset.manufacturer && asset.manufacturer.toLowerCase().includes(query)) ||
            (asset.location && asset.location.toLowerCase().includes(query)) ||
            (asset.categoryName && asset.categoryName.toLowerCase().includes(query)) ||
            (asset.propertyName && asset.propertyName.toLowerCase().includes(query))
        );
    });

    /**
     * Resolves the full asset object for the row currently being dragged.
     *
     * Used by the DragOverlay to render a floating preview card.  Looks up
     * from `filteredAssets` (not `assets`) because filteredAssets is the set
     * visible in the table — the drag ID must correspond to a visible row.
     *
     * Returns null when no drag is active, which hides the overlay.
     */
    const draggedAsset = activeDragId
        ? filteredAssets.find(a => `asset-${a.assetId}` === activeDragId)
        : null;

    /**
     * Returns the active sub-categories for the category currently selected
     * in the create/edit asset form.
     *
     * Sub-categories are filtered by `isActive` so that archived sub-categories
     * do not appear in the form even if they are still stored on existing assets.
     * The result is sorted alphabetically for consistency with the category list.
     *
     * @returns {Array} Array of active sub-category objects
     */
    const getSubCategoriesForSelectedCategory = () => {
        if (!formData.assetCategoryId) return [];
        const selectedCategory = categories.find(c => c.assetCategoryId === parseInt(formData.assetCategoryId));
        return [...(selectedCategory?.subCategories?.filter(sc => sc.isActive) || [])]
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    };

    /**
     * Maps an asset status string to a CSS badge class.
     *
     * Status values come from the AssetStatus enum on the server.  Having a
     * single mapping function means a new status only needs to be added here
     * and the SCSS file, not scattered across JSX.
     *
     * @param {string} status - Asset status value from the server enum
     * @returns {string} CSS class name for the badge
     */
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'Active': return 'badge-success';
            case 'Inactive': return 'badge-secondary';
            case 'UnderRepair': return 'badge-warning';
            case 'Decommissioned': return 'badge-danger';
            default: return 'badge-secondary';
        }
    };

    /**
     * Returns a service status indicator for an asset row.
     *
     * This client-side calculation uses `nextServiceDueDate` which is computed
     * by the server (based on lastServiceDate + serviceInterval).  It mirrors
     * the thresholds used in fetchScheduleOverdueSummary and filteredAssets so
     * all three views (row colour, service status column, dashboard card)
     * are consistent.
     *
     * Priority order:
     *   1. Not configured for servicing → N/A
     *   2. Under repair → Under Repair (overrides due date display)
     *   3. Past due date → Overdue
     *   4. Within 30 days → Due Soon
     *   5. Otherwise → OK
     *
     * @param {Object} asset - Asset row object from the server
     * @returns {{ label: string, className: string }}
     */
    const getServiceStatus = (asset) => {
        if (!asset.requiresServicing || !asset.nextServiceDueDate) {
            return { label: 'N/A', className: 'service-na' };
        }
        if (asset.isUnderRepair) {
            return { label: 'Under Repair', className: 'service-repair' };
        }
        const dueDate = new Date(asset.nextServiceDueDate);
        const now = new Date();
        const daysUntilDue = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));

        if (daysUntilDue < 0) return { label: 'Overdue', className: 'service-overdue' };
        if (daysUntilDue <= 30) return { label: 'Due Soon', className: 'service-due-soon' };
        return { label: 'OK', className: 'service-ok' };
    };

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className={`asset-management-page ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
            {/* ── Page Header ──────────────────────────────────────── */}
            <div className="page-header">
                <h1><FaCog /> Asset Management</h1>
                <div className="header-actions">
                    {assetSignalRConnected && <span className="live-indicator" title="Real-time updates active">● Live</span>}
                    <button className="btn-secondary" onClick={() => setSidebarOpen(!sidebarOpen)}>
                        {sidebarOpen ? <FaChevronLeft /> : <FaChevronRight />}
                        {sidebarOpen ? 'Hide Groups' : 'Show Groups'}
                    </button>
                    {hasPermission('/operations/asset-import-export') && (
                            <button
                                className="btn-secondary"
                                onClick={() => navigate('/operations/asset-import-export')}
                                title="Import or export assets via CSV"
                            >
                                <FaUpload /> Import / Export
                            </button>
                    )}
                    <button className="btn-create" onClick={handleCreateNew}>
                        <FaPlus /> New Asset
                    </button>
                </div>
            </div>

            {/* ── Two-Panel Layout ─────────────────────────────────── */}
            <div className="am-layout">

                {/* ── Left Sidebar: Groups ──────────────────────────── */}
                {sidebarOpen && (
                    <aside className="am-sidebar">
                        <div className="sidebar-header">
                            <h3><FaLayerGroup /> Groups</h3>
                            {filterPropertyId && (
                                <button className="btn-sm btn-create" onClick={handleCreateGroup} title="New Group">
                                    <FaPlus />
                                </button>
                            )}
                        </div>

                        {!filterPropertyId ? (
                            <p className="sidebar-hint"><FaInfoCircle /> Select a property to manage groups</p>
                        ) : groups.length === 0 ? (
                            <p className="sidebar-hint">No groups yet. Create one to start organising assets.</p>
                        ) : (
                            <div className="group-cards">
                                {groups.map(group => (
                                    <DroppableGroupCard
                                        key={group.assetGroupId}
                                        group={group}
                                        isExpanded={expandedGroupId === group.assetGroupId}
                                        selectedGroup={selectedGroup}
                                        onToggleExpand={() => {
                                            if (expandedGroupId === group.assetGroupId) {
                                                setExpandedGroupId(null);
                                            } else {
                                                setExpandedGroupId(group.assetGroupId);
                                                if (!selectedGroup || selectedGroup.assetGroupId !== group.assetGroupId) {
                                                    handleViewGroup(group);
                                                }
                                            }
                                        }}
                                        onEdit={() => handleEditGroup(group)}
                                        onBulkService={() => handleCreateBulkService(group)}
                                        onRemoveMember={(assetId) => handleRemoveGroupMembers(group.assetGroupId, [assetId])}
                                        isDragActive={!!activeDragId}
                                    />
                                ))}
                            </div>
                        )}
                    </aside>
                )}

                {/* ── Main Content Area ─────────────────────────────── */}
                <main className="am-main">
                    {/* Filters */}
                    <div className="filters-section">
                        <div className="search-box">
                            <FaSearch />
                            <input
                                type="text"
                                placeholder="Search assets..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button className="search-clear" onClick={() => setSearchQuery('')}><FaTimes /></button>
                            )}
                        </div>
                        <div className="filter-controls">
                            <select
                                value={filterPropertyId}
                                onChange={(e) => setFilterPropertyId(e.target.value)}
                                className="filter-select"
                            >
                                <option value="">All Properties</option>
                                {properties.map(p => (
                                    <option key={p.propertyID || p.propertyId} value={p.propertyID || p.propertyId}>
                                        {p.propertyName}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={filterCategoryId}
                                onChange={(e) => setFilterCategoryId(e.target.value)}
                                className="filter-select"
                            >
                                <option value="">All Categories</option>
                                {categories.filter(c => c.isActive).map(c => (
                                    <option key={c.assetCategoryId} value={c.assetCategoryId}>{c.name}</option>
                                ))}
                            </select>
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="filter-select"
                            >
                                <option value="">All Statuses</option>
                                {referenceData?.statuses?.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                            <label className="filter-checkbox">
                                <input
                                    type="checkbox"
                                    checked={showInactive}
                                    onChange={(e) => setShowInactive(e.target.checked)}
                                />
                                Show Inactive
                            </label>
                        </div>
                    </div>

                    {/* Dashboard Summary — clickable cards for quick filtering */}
                    {dashboardStats && (
                        <div className="dashboard-summary">
                            <button
                                className={`summary-card summary-overdue ${dashboardFilter === 'overdue' ? 'active-filter' : ''}`}
                                onClick={() => handleDashboardCardClick('overdue')}
                            >
                                <div className="summary-value">{dashboardStats.overdueServiceCount ?? 0}</div>
                                <div className="summary-label">Overdue Services</div>
                                {dashboardFilter === 'overdue' && <FaFilter className="filter-indicator" />}
                            </button>
                            <button
                                className={`summary-card summary-due-soon ${dashboardFilter === 'dueSoon' ? 'active-filter' : ''}`}
                                onClick={() => handleDashboardCardClick('dueSoon')}
                            >
                                <div className="summary-value">{dashboardStats.dueSoonServiceCount ?? 0}</div>
                                <div className="summary-label">Due Within 30 Days</div>
                                {dashboardFilter === 'dueSoon' && <FaFilter className="filter-indicator" />}
                            </button>
                            <button
                                className={`summary-card summary-warranty-expired ${dashboardFilter === 'warrantyExpired' ? 'active-filter' : ''}`}
                                onClick={() => handleDashboardCardClick('warrantyExpired')}
                            >
                                <div className="summary-value">{dashboardStats.expiredWarrantyCount ?? 0}</div>
                                <div className="summary-label">Expired Warranties</div>
                                {dashboardFilter === 'warrantyExpired' && <FaFilter className="filter-indicator" />}
                            </button>
                            <button
                                className={`summary-card summary-under-repair ${dashboardFilter === 'underRepair' ? 'active-filter' : ''}`}
                                onClick={() => handleDashboardCardClick('underRepair')}
                            >
                                <div className="summary-value">{dashboardStats.underRepairCount ?? 0}</div>
                                <div className="summary-label">Under Repair</div>
                                {dashboardFilter === 'underRepair' && <FaFilter className="filter-indicator" />}
                            </button>
                            <div className="summary-card summary-total">
                                <div className="summary-value">{dashboardStats.totalActiveAssets ?? 0}</div>
                                <div className="summary-label">Total Active Assets</div>
                            </div>
                        </div>
                    )}

                    {/* Active Filter Banner */}
                    {dashboardFilter && (
                        <div className="active-filter-banner">
                            <FaFilter />
                            <span>
                                Showing: {dashboardFilter === 'overdue' ? 'Overdue Services' :
                                    dashboardFilter === 'dueSoon' ? 'Due Within 30 Days' :
                                    dashboardFilter === 'warrantyExpired' ? 'Expired Warranties' :
                                    dashboardFilter === 'underRepair' ? 'Under Repair' : ''}
                            </span>
                            <button onClick={() => setDashboardFilter(null)} className="clear-filter-btn">
                                <FaTimes /> Clear
                            </button>
                        </div>
                    )}

                    {/* Selection Toolbar */}
                    {selectedAssetIds.length > 0 && (
                        <div className="selection-toolbar">
                            <span>{selectedAssetIds.length} asset(s) selected</span>
                            <div className="selection-actions">
                                {filterPropertyId && groups.length > 0 && (
                                    <div className="group-picker-wrapper" style={{ position: 'relative' }}>
                                        <button className="btn-sm btn-primary" onClick={() => setShowGroupPicker(!showGroupPicker)}>
                                            <FaLayerGroup /> Add to Group
                                        </button>
                                        {showGroupPicker && (
                                            <ul className="group-picker-dropdown">
                                                {groups.map(g => (
                                                    <li key={g.assetGroupId} onClick={() => handleAddSelectedToGroup(g.assetGroupId)}>
                                                        {g.name} ({g.memberCount} members)
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}
                                <button className="btn-sm btn-secondary" onClick={() => setSelectedAssetIds([])}>
                                    <FaTimes /> Clear Selection
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Error State */}
                    {error && (
                        <div className="alert alert-danger">
                            <FaExclamationTriangle /> {error}
                        </div>
                    )}

                    {/* Loading State */}
                    {loading && (
                        <div className="loading-container">
                            <FaSpinner className="fa-spin fa-2x" />
                            <p>Loading assets...</p>
                        </div>
                    )}

                    {/* Asset Table with Draggable Rows */}
                    {!loading && !error && (
                        <div className="table-responsive">
                            {sidebarOpen && filterPropertyId && groups.length > 0 && (
                                <p className="drag-hint"><FaGripVertical /> Drag assets to a group in the sidebar to add them</p>
                            )}
                            <table className="asset-table">
                                <thead>
                                    <tr>
                                        <th className="checkbox-col">
                                            <input
                                                type="checkbox"
                                                checked={selectedAssetIds.length > 0 && selectedAssetIds.length === filteredAssets.length}
                                                onChange={toggleSelectAll}
                                                title="Select all"
                                            />
                                        </th>
                                        {sidebarOpen && filterPropertyId && groups.length > 0 && <th className="drag-col"></th>}
                                        <th>Name</th>
                                        <th>Property</th>
                                        <th>Category</th>
                                        <th>Location</th>
                                        <th>Status</th>
                                        <th>Service Status</th>
                                        <th>Warranty</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAssets.length === 0 ? (
                                        <tr>
                                            <td colSpan={sidebarOpen && filterPropertyId && groups.length > 0 ? 10 : 9} className="empty-state">
                                                {assets.length === 0
                                                    ? 'No assets found. Click "New Asset" to add your first asset.'
                                                    : dashboardFilter
                                                        ? 'No assets match the current dashboard filter.'
                                                        : 'No assets match the current filters.'}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredAssets.map(asset => {
                                            const serviceStatus = getServiceStatus(asset);
                                            const showDragHandle = sidebarOpen && filterPropertyId && groups.length > 0;
                                            return (
                                                <DraggableAssetRow
                                                    key={asset.assetId}
                                                    asset={asset}
                                                    serviceStatus={serviceStatus}
                                                    isSelected={selectedAssetIds.includes(asset.assetId)}
                                                    onToggleSelect={() => toggleAssetSelection(asset.assetId)}
                                                    onView={() => handleViewDetail(asset)}
                                                    onEdit={() => handleEdit(asset)}
                                                    onToggleActive={() => handleToggleActive(asset)}
                                                    onQuickService={() => handleQuickService(asset)}
                                                    getStatusBadgeClass={getStatusBadgeClass}
                                                    showDragHandle={showDragHandle}
                                                    quickServiceAssetId={quickServiceAssetId}
                                                    serviceFormData={serviceFormData}
                                                    setServiceFormData={setServiceFormData}
                                                    referenceData={referenceData}
                                                    saving={saving}
                                                    onSaveQuickService={() => handleSaveQuickService(asset.assetId)}
                                                    onCancelQuickService={() => setQuickServiceAssetId(null)}
                                                    scheduleOverdue={scheduleOverdueMap[asset.assetId]}
                                                />
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="pagination-controls">
                                    <button className="btn-sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(1)}>« First</button>
                                    <button className="btn-sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>‹ Prev</button>
                                    <span className="page-info">Page {currentPage} of {totalPages} ({totalCount} assets)</span>
                                    <button className="btn-sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next ›</button>
                                    <button className="btn-sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}>Last »</button>
                                </div>
                            )}
                        </div>
                    )}
                </main>
            </div>

            {/* Drag Overlay — shows a floating preview of the dragged asset */}
            <DragOverlay>
                {draggedAsset ? (
                    <div className="drag-overlay-card">
                        <FaGripVertical />
                        <strong>{draggedAsset.name}</strong>
                        {draggedAsset.assetTag && <span className="asset-tag"><FaTag /> {draggedAsset.assetTag}</span>}
                    </div>
                ) : null}
            </DragOverlay>

            {/* Create/Edit Asset Modal */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{formData.assetId ? 'Edit Asset' : 'Create New Asset'}</h2>
                            <button className="btn-close" onClick={() => setShowCreateModal(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            {/* Basic Information */}
                            <fieldset className="form-section">
                                <legend><FaInfoCircle className="legend-icon" /> Basic Information</legend>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Asset Name <span className="required">*</span></label>
                                        <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Main Escalator - North Wing" />
                                    </div>
                                    <div className="form-group">
                                        <label>Asset Tag</label>
                                        <input type="text" value={formData.assetTag} onChange={(e) => setFormData({ ...formData, assetTag: e.target.value })} placeholder="Barcode or tag reference" />
                                    </div>
                                    <div className="form-group">
                                        <label>Serial Number</label>
                                        <input type="text" value={formData.serialNumber} onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Manufacturer</label>
                                        <input type="text" value={formData.manufacturer} onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })} placeholder="e.g. Otis, Carrier, Siemens" />
                                    </div>
                                    <div className="form-group">
                                        <label>Model</label>
                                        <input type="text" value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Quantity</label>
                                        <input type="number" min={1} value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} title="Number of identical items this record represents (e.g. 12 fire extinguishers at one location)" />
                                    </div>
                                    <div className="form-group full-width">
                                        <label>Description</label>
                                        <textarea rows={3} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                                    </div>
                                </div>
                            </fieldset>

                            {/* Classification */}
                            <fieldset className="form-section">
                                <legend><FaTag className="legend-icon" /> Classification</legend>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Category <span className="required">*</span></label>
                                        <select value={formData.assetCategoryId} onChange={(e) => setFormData({ ...formData, assetCategoryId: e.target.value, assetSubCategoryId: '' })}>
                                            <option value="">Select Category</option>
                                            {categories.filter(c => c.isActive).map(c => (
                                                <option key={c.assetCategoryId} value={c.assetCategoryId}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Sub-Category</label>
                                        <select value={formData.assetSubCategoryId} onChange={(e) => setFormData({ ...formData, assetSubCategoryId: e.target.value })}>
                                            <option value="">Select Sub-Category</option>
                                            {getSubCategoriesForSelectedCategory().map(sc => (
                                                <option key={sc.assetSubCategoryId} value={sc.assetSubCategoryId}>{sc.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </fieldset>

                            {/* Location & Ownership */}
                            <fieldset className="form-section">
                                <legend><FaMapMarkerAlt className="legend-icon" /> Location & Ownership</legend>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Property <span className="required">*</span></label>
                                        <select value={formData.propertyId} onChange={(e) => setFormData({ ...formData, propertyId: e.target.value, mdaTenantId: null, tenantName: '', mdaRentableUnitId: null, rentableUnitName: '' })}>
                                            <option value="">Select Property</option>
                                            {properties.map(p => (
                                                <option key={p.propertyID || p.propertyId} value={p.propertyID || p.propertyId}>{p.propertyName}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Location Within Property</label>
                                        <input type="text" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="e.g. Level 2, North Wing" />
                                    </div>
                                    <div className="form-group" ref={rentableUnitDropdownRef} style={{ position: 'relative' }}>
                                        <label>Rentable Unit</label>
                                        <input
                                            type="text"
                                            value={rentableUnitSearch || formData.rentableUnitName}
                                            onChange={(e) => handleRentableUnitSearchChange(e.target.value)}
                                            onFocus={() => { if (formData.propertyId) fetchRentableUnits(rentableUnitSearch); }}
                                            placeholder={formData.propertyId ? 'Search unit number...' : 'Select a property first'}
                                            disabled={!formData.propertyId}
                                        />
                                        {formData.mdaRentableUnitId && (
                                            <button type="button" className="clear-selection-btn" onClick={() => { setFormData(prev => ({ ...prev, mdaRentableUnitId: null, rentableUnitName: '' })); setRentableUnitSearch(''); }} title="Clear selection">✕</button>
                                        )}
                                        {showRentableUnitDropdown && rentableUnitResults.length > 0 && (
                                            <ul className="search-dropdown">
                                                {rentableUnitResults.map(unit => (
                                                    <li key={unit.propertyRentableUnitID} onClick={() => selectRentableUnit(unit)} className={formData.mdaRentableUnitId === unit.propertyRentableUnitID ? 'selected' : ''}>
                                                        <strong>{unit.propertyRentableUnitNo}</strong>
                                                        {unit.description && <span className="dropdown-detail"> — {unit.description}</span>}
                                                        {unit.area && <span className="dropdown-meta">{unit.area} m²</span>}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {showRentableUnitDropdown && rentableUnitLoading && <div className="search-dropdown-loading"><FaSpinner className="spin" /> Searching...</div>}
                                    </div>
                                    <div className="form-group">
                                        <label className="checkbox-label">
                                            <input type="checkbox" checked={formData.belongsToTenant} onChange={(e) => setFormData({ ...formData, belongsToTenant: e.target.checked, mdaTenantId: null, tenantName: '' })} />
                                            Belongs to Tenant
                                        </label>
                                    </div>
                                    {formData.belongsToTenant && (
                                        <div className="form-group" style={{ position: 'relative' }}>
                                            <label>Tenant Name</label>
                                            <input
                                                ref={tenantSearchRef}
                                                type="text"
                                                value={formData.tenantName}
                                                onChange={(e) => handleTenantSearchChange(e.target.value)}
                                                onFocus={() => { if (formData.tenantName.length >= 2) searchTenants(formData.tenantName); }}
                                                placeholder={formData.propertyId ? 'Search tenant name...' : 'Select a property first'}
                                                disabled={!formData.propertyId}
                                                className={formData.mdaTenantId ? 'input-linked' : ''}
                                            />
                                            {formData.mdaTenantId && (
                                                <span className="linked-badge" title="Linked to MDA tenant">✓ Linked</span>
                                            )}
                                            {showTenantDropdown && tenantSearchResults.length > 0 && (
                                                <ul className="search-dropdown" ref={tenantDropdownRef}>
                                                    {tenantSearchResults.map(t => (
                                                        <li key={t.tenantID} onClick={() => selectTenant(t)}>
                                                            <strong>{t.listOrTradingAsName}</strong>
                                                            {t.lesseeName !== t.listOrTradingAsName && <span className="dropdown-detail"> ({t.lesseeName})</span>}
                                                            {t.mainUnitNo && <span className="dropdown-meta">Unit: {t.mainUnitNo}</span>}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                            {tenantSearchLoading && <div className="search-dropdown-loading"><FaSpinner className="spin" /> Searching...</div>}
                                        </div>
                                    )}
                                </div>
                            </fieldset>

                            {/* Commissioning */}
                            <fieldset className="form-section">
                                <legend><FaCalendarCheck className="legend-icon" /> Commissioning</legend>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="checkbox-label">
                                            <input type="checkbox" checked={formData.isNew} onChange={(e) => setFormData({ ...formData, isNew: e.target.checked })} />
                                            New Asset (requires commissioning)
                                        </label>
                                    </div>
                                    <div className="form-group">
                                        <label>Installation Date</label>
                                        <input type="date" value={formData.installationDate} onChange={(e) => setFormData({ ...formData, installationDate: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Commissioning Date</label>
                                        <input type="date" value={formData.commissioningDate} onChange={(e) => setFormData({ ...formData, commissioningDate: e.target.value })} />
                                    </div>
                                </div>
                                {formData.isNew && (
                                    <div className="commissioning-upload-section">
                                        {formData.assetId ? (
                                            <div className="commissioning-upload-controls">
                                                <p className="info-text"><FaUpload /> Upload commissioning documents for this asset:</p>
                                                <div className="upload-row">
                                                    <input
                                                        type="file"
                                                        id="commissioningFileInput"
                                                        style={{ display: 'none' }}
                                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.csv,.zip"
                                                        onChange={async (e) => {
                                                            const file = e.target.files?.[0];
                                                            if (!file) return;
                                                            try {
                                                                setSaving(true);
                                                                const uploadData = new FormData();
                                                                uploadData.append('file', file);
                                                                uploadData.append('documentType', 'CommissioningCertificate');
                                                                uploadData.append('notes', 'Uploaded during asset edit');
                                                                const res = await apiFetch(
                                                                    `/api/AssetManagement/assets/${formData.assetId}/documents/upload`,
                                                                    { method: 'POST', body: uploadData }
                                                                );
                                                                const result = await res.json();
                                                                if (result.success) {
                                                                    toast.success('Commissioning document uploaded successfully');
                                                                } else {
                                                                    toast.error(result.message || 'Failed to upload document');
                                                                }
                                                            } catch (catchError) {
                                                                Sentry.captureException(catchError);
                                                                toast.error('Failed to upload document. Please try again.');
                                                            } finally {
                                                                setSaving(false);
                                                                e.target.value = '';
                                                            }
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn-upload"
                                                        onClick={() => document.getElementById('commissioningFileInput')?.click()}
                                                        disabled={saving}
                                                    >
                                                        <FaUpload /> Upload Commissioning Document
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="info-text muted"><FaFileAlt /> Save the asset first, then you can upload commissioning documents.</p>
                                        )}
                                    </div>
                                )}
                            </fieldset>

                            {/* Service Configuration */}
                            <fieldset className="form-section">
                                <legend><FaWrench className="legend-icon" /> Service Configuration</legend>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="checkbox-label">
                                            <input type="checkbox" checked={formData.requiresServicing} onChange={(e) => setFormData({ ...formData, requiresServicing: e.target.checked })} />
                                            Requires Regular Servicing
                                        </label>
                                    </div>
                                    {formData.requiresServicing && (
                                        <>
                                            <div className="form-group">
                                                <label>Service Interval</label>
                                                <div className="interval-inputs">
                                                    <input type="number" min={1} value={formData.serviceIntervalValue} onChange={(e) => setFormData({ ...formData, serviceIntervalValue: e.target.value })} placeholder="e.g. 6" />
                                                    <select value={formData.serviceIntervalUnit} onChange={(e) => setFormData({ ...formData, serviceIntervalUnit: e.target.value })}>
                                                        <option value="">Unit</option>
                                                        {referenceData?.serviceIntervalUnits?.map(u => (
                                                            <option key={u} value={u}>{u}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>Last Service Date</label>
                                                <input type="date" value={formData.lastServiceDate} onChange={(e) => setFormData({ ...formData, lastServiceDate: e.target.value })} />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </fieldset>

                            {/* Warranty */}
                            <fieldset className="form-section">
                                <legend><FaShieldAlt className="legend-icon" /> Warranty</legend>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="checkbox-label">
                                            <input type="checkbox" checked={formData.hasWarranty} onChange={(e) => setFormData({ ...formData, hasWarranty: e.target.checked })} />
                                            Has Warranty
                                        </label>
                                    </div>
                                    {formData.hasWarranty && (
                                        <>
                                            <div className="form-group">
                                                <label>Warranty Expiry Date</label>
                                                <input type="date" value={formData.warrantyExpiryDate} onChange={(e) => setFormData({ ...formData, warrantyExpiryDate: e.target.value })} />
                                            </div>
                                            <div className="form-group full-width">
                                                <label>Warranty Notes</label>
                                                <textarea rows={2} value={formData.warrantyNotes} onChange={(e) => setFormData({ ...formData, warrantyNotes: e.target.value })} />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </fieldset>

                            {/* Cost & Notes */}
                            <fieldset className="form-section">
                                <legend><FaMoneyBillWave className="legend-icon" /> Cost & Notes</legend>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Purchase Cost (ZAR)</label>
                                        <div className="input-with-addon">
                                            <span className="input-prefix">R</span>
                                            <input type="number" step="0.01" min={0} value={formData.purchaseCost} onChange={(e) => setFormData({ ...formData, purchaseCost: e.target.value })} placeholder="0.00" />
                                        </div>
                                        <label className="checkbox-label vat-checkbox">
                                            <input type="checkbox" checked={formData.purchaseCostIncludesVat} onChange={(e) => setFormData({ ...formData, purchaseCostIncludesVat: e.target.checked })} />
                                            Includes VAT
                                        </label>
                                    </div>
                                    <div className="form-group full-width">
                                        <label>Notes</label>
                                        <textarea rows={3} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes about this asset..." />
                                    </div>
                                </div>
                            </fieldset>

                            {/* Ownership & Responsibility */}
                            <fieldset className="form-section">
                                <legend><FaBuilding className="legend-icon" /> Ownership &amp; Responsibility</legend>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Asset Owner</label>
                                        <select value={formData.assetOwnerId} onChange={(e) => setFormData({ ...formData, assetOwnerId: e.target.value })}>
                                            <option value="">— Not assigned —</option>
                                            {usersList.map(u => (
                                                <option key={u.userID} value={u.userID}>{u.name}</option>
                                            ))}
                                        </select>
                                        <span className="field-help">The person responsible for day-to-day ownership of this asset.</span>
                                    </div>
                                    <div className="form-group">
                                        <label>Asset Manager</label>
                                        <select value={formData.assetManagerId} onChange={(e) => setFormData({ ...formData, assetManagerId: e.target.value })}>
                                            <option value="">— Not assigned —</option>
                                            {usersList.map(u => (
                                                <option key={u.userID} value={u.userID}>{u.name}</option>
                                            ))}
                                        </select>
                                        <span className="field-help">The person with management authority and accountability for this asset.</span>
                                    </div>
                                </div>
                            </fieldset>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowCreateModal(false)} disabled={saving}>Cancel</button>
                            <button className="btn-primary" onClick={handleSaveAsset} disabled={saving}>
                                {saving ? <><FaSpinner className="fa-spin" /> Saving...</> : (formData.assetId ? 'Update Asset' : 'Create Asset')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Asset Detail Modal */}
            {showDetailModal && selectedAsset && (
                <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
                    <div className="modal-dialog modal-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2><FaBuilding /> {selectedAsset.name}</h2>
                            <button className="btn-close" onClick={() => setShowDetailModal(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            {/* Detail Tabs */}
                            <div className="detail-tabs">
                                <button className={`tab-btn ${detailTab === 'details' ? 'active' : ''}`} onClick={() => setDetailTab('details')}>
                                    <FaEye /> Details
                                </button>
                                <button className={`tab-btn ${detailTab === 'services' ? 'active' : ''}`} onClick={() => setDetailTab('services')}>
                                    <FaWrench /> Service History
                                </button>
                                <button className={`tab-btn ${detailTab === 'audit' ? 'active' : ''}`} onClick={() => setDetailTab('audit')}>
                                    <FaHistory /> Audit Trail
                                </button>
                                <button className={`tab-btn ${detailTab === 'documents' ? 'active' : ''}`} onClick={() => setDetailTab('documents')}>
                                    <FaFileAlt /> Documents ({selectedAsset.documents?.length || 0})
                                </button>
                                <button
                                    className={`tab-btn ${detailTab === 'schedules' ? 'active' : ''}`}
                                    onClick={() => { setDetailTab('schedules'); fetchAssetSchedules(selectedAsset.assetId); }}
                                >
                                    <FaCogs /> Service Schedules
                                    {scheduleOverdueMap[selectedAsset.assetId]?.isOverdue && (
                                        <span className="tab-badge-overdue" title="Overdue schedule(s)">!</span>
                                    )}
                                </button>
                            </div>

                            {/* Details Tab */}
                            {detailTab === 'details' && (
                                <div className="detail-content">
                                    <div className="detail-grid">
                                        <div className="detail-section">
                                            <h3>Identification</h3>
                                            <div className="detail-fields">
                                                <div className="detail-field"><span>Asset Tag:</span> {selectedAsset.assetTag || 'N/A'}</div>
                                                <div className="detail-field"><span>Serial Number:</span> {selectedAsset.serialNumber || 'N/A'}</div>
                                                <div className="detail-field"><span>Manufacturer:</span> {selectedAsset.manufacturer || 'N/A'}</div>
                                                <div className="detail-field"><span>Model:</span> {selectedAsset.model || 'N/A'}</div>
                                            </div>
                                        </div>
                                        <div className="detail-section">
                                            <h3>Location</h3>
                                            <div className="detail-fields">
                                                <div className="detail-field"><span>Property:</span> {selectedAsset.propertyName || 'N/A'}</div>
                                                <div className="detail-field"><span>Location:</span> {selectedAsset.location || 'N/A'}</div>
                                                <div className="detail-field"><span>Rentable Unit:</span> {selectedAsset.rentableUnitName || 'N/A'}</div>
                                                <div className="detail-field"><span>Owner:</span> {selectedAsset.belongsToTenant ? `Tenant: ${selectedAsset.tenantName}` : 'Property'}</div>
                                            </div>
                                        </div>
                                        <div className="detail-section">
                                            <h3>Classification</h3>
                                            <div className="detail-fields">
                                                <div className="detail-field"><span>Category:</span> {selectedAsset.categoryName || 'N/A'}</div>
                                                <div className="detail-field"><span>Sub-Category:</span> {selectedAsset.subCategoryName || 'N/A'}</div>
                                                <div className="detail-field"><span>Status:</span> <span className={`status-badge ${getStatusBadgeClass(selectedAsset.status)}`}>{selectedAsset.status}</span></div>
                                            </div>
                                        </div>
                                        <div className="detail-section">
                                            <h3>Service Information</h3>
                                            <div className="detail-fields">
                                                <div className="detail-field"><span>Requires Servicing:</span> {selectedAsset.requiresServicing ? 'Yes' : 'No'}</div>
                                                {selectedAsset.requiresServicing && (
                                                    <>
                                                        <div className="detail-field"><span>Service Interval:</span> {selectedAsset.serviceIntervalValue} {selectedAsset.serviceIntervalUnit}</div>
                                                        <div className="detail-field"><span>Last Service:</span> {selectedAsset.lastServiceDate ? new Date(selectedAsset.lastServiceDate).toLocaleDateString() : 'Never'}</div>
                                                        <div className="detail-field"><span>Next Due:</span> {selectedAsset.nextServiceDueDate ? new Date(selectedAsset.nextServiceDueDate).toLocaleDateString() : 'N/A'}</div>
                                                    </>
                                                )}
                                                <div className="detail-field"><span>Under Repair:</span> {selectedAsset.isUnderRepair ? 'Yes' : 'No'}</div>
                                            </div>
                                        </div>
                                        <div className="detail-section">
                                            <h3>Warranty</h3>
                                            <div className="detail-fields">
                                                <div className="detail-field"><span>Has Warranty:</span> {selectedAsset.hasWarranty ? 'Yes' : 'No'}</div>
                                                {selectedAsset.hasWarranty && (
                                                    <>
                                                        <div className="detail-field"><span>Expiry:</span> {selectedAsset.warrantyExpiryDate ? new Date(selectedAsset.warrantyExpiryDate).toLocaleDateString() : 'N/A'}</div>
                                                        {selectedAsset.warrantyNotes && <div className="detail-field"><span>Notes:</span> {selectedAsset.warrantyNotes}</div>}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="detail-section">
                                            <h3>Cost Information</h3>
                                            <div className="detail-fields">
                                                <div className="detail-field"><span>Purchase Cost:</span> {selectedAsset.purchaseCost ? `R ${selectedAsset.purchaseCost.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}${selectedAsset.purchaseCostIncludesVat ? ' (incl. VAT)' : ' (excl. VAT)'}` : 'N/A'}</div>
                                            </div>
                                        </div>
                                    </div>
                                    {selectedAsset.description && (
                                        <div className="detail-section full-width">
                                            <h3>Description</h3>
                                            <p>{selectedAsset.description}</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Services Tab */}
                            {detailTab === 'services' && (
                                <div className="services-content">
                                    <div className="section-header">
                                        <h3>Service Records</h3>
                                        <button className="btn-create" onClick={handleCreateServiceRecord}>
                                            <FaPlus /> Add Service Record
                                        </button>
                                    </div>
                                    {selectedAsset.serviceRecords?.length > 0 ? (
                                        <div className="service-records-list">
                                            {selectedAsset.serviceRecords.map(record => {
                                                const isExpanded = expandedRecordId === record.assetServiceRecordId;
                                                const badge = getApprovalBadge(record.approvalStatus);
                                                return (
                                                    <div key={record.assetServiceRecordId} className={`service-record-card ${isExpanded ? 'expanded' : ''}`}>
                                                        <div className="service-record-header" onClick={() => setExpandedRecordId(isExpanded ? null : record.assetServiceRecordId)}>
                                                            <div className="sr-summary">
                                                                <span className={`type-badge ${record.recordType === 'RepairAndMaintenance' ? 'type-rnm' : 'type-service'}`}>
                                                                    {record.recordType === 'RepairAndMaintenance' ? 'R&M' : 'Service'}
                                                                </span>
                                                                <strong>{record.title}</strong>
                                                                {record.bulkServiceReference && (
                                                                    <span className="bulk-ref-badge" title={`Bulk Ref: ${record.bulkServiceReference}`}>
                                                                        <FaLayerGroup /> Bulk
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="sr-meta">
                                                                <span>{record.serviceDate ? new Date(record.serviceDate).toLocaleDateString() : 'Not serviced'}</span>
                                                                <span className={`approval-badge ${badge.className}`}>{badge.label}</span>
                                                                {record.cost ? <span className="sr-cost">R {record.cost.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span> : null}
                                                                <span className="expand-arrow">{isExpanded ? '▲' : '▼'}</span>
                                                            </div>
                                                        </div>
                                                        {isExpanded && (
                                                            <div className="service-record-detail">
                                                                <div className="sr-detail-grid">
                                                                    <div className="sr-field"><label>Description:</label><span>{record.description || 'N/A'}</span></div>
                                                                    <div className="sr-field"><label>Provider:</label><span>{record.serviceProviderName || 'N/A'}</span></div>
                                                                    <div className="sr-field"><label>Reference:</label><span>{record.providerReference || 'N/A'}</span></div>
                                                                    <div className="sr-field"><label>Due Date:</label><span>{record.serviceDueDate ? new Date(record.serviceDueDate).toLocaleDateString() : 'N/A'}</span></div>
                                                                    <div className="sr-field"><label>Service Date:</label><span>{record.serviceDate ? new Date(record.serviceDate).toLocaleDateString() : 'N/A'}</span></div>
                                                                    <div className="sr-field"><label>Cost:</label><span>{record.cost ? `R ${record.cost.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}${record.costIncludesVat ? ' (incl. VAT)' : ' (excl. VAT)'}` : 'N/A'}</span></div>
                                                                    {record.costNotes && <div className="sr-field full-width"><label>Cost Notes:</label><span>{record.costNotes}</span></div>}
                                                                    {record.notes && <div className="sr-field full-width"><label>Notes:</label><span>{record.notes}</span></div>}
                                                                    {record.rejectionReason && (
                                                                        <div className="sr-field full-width sr-rejection">
                                                                            <label><FaExclamationTriangle /> Rejection Reason:</label>
                                                                            <span>{record.rejectionReason}</span>
                                                                        </div>
                                                                    )}
                                                                    {record.submittedDate && <div className="sr-field"><label>Submitted:</label><span>{new Date(record.submittedDate).toLocaleString()}</span></div>}
                                                                    {record.reviewedDate && <div className="sr-field"><label>Reviewed:</label><span>{new Date(record.reviewedDate).toLocaleString()}</span></div>}
                                                                    {record.bulkServiceReference && <div className="sr-field"><label>Bulk Ref:</label><span className="mono">{record.bulkServiceReference}</span></div>}
                                                                </div>
                                                                <div className="sr-actions">
                                                                    {record.approvalStatus === 'Pending' && (
                                                                        <>
                                                                            <button className="btn-sm btn-primary" onClick={() => handleSubmitServiceRecord(record.assetServiceRecordId)} disabled={workflowLoading}>
                                                                                {workflowLoading ? <FaSpinner className="fa-spin" /> : null} Submit for Review
                                                                            </button>
                                                                            {record.bulkServiceReference && (
                                                                                <button className="btn-sm btn-secondary" onClick={() => handleSubmitBulkRecords(record.bulkServiceReference)} disabled={workflowLoading}>
                                                                                    <FaLayerGroup /> Submit Entire Batch
                                                                                </button>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                    {record.approvalStatus === 'Submitted' && (
                                                                        <>
                                                                            <button className="btn-sm btn-success" onClick={() => handleApproveServiceRecord(record.assetServiceRecordId)} disabled={workflowLoading}>
                                                                                {workflowLoading ? <FaSpinner className="fa-spin" /> : null} Approve
                                                                            </button>
                                                                            <button className="btn-sm btn-danger" onClick={() => handleOpenRejectModal(record.assetServiceRecordId, record.bulkServiceReference)} disabled={workflowLoading}>
                                                                                Reject
                                                                            </button>
                                                                            {record.bulkServiceReference && (
                                                                                <>
                                                                                    <button className="btn-sm btn-secondary" onClick={() => handleApproveBulkRecords(record.bulkServiceReference)} disabled={workflowLoading}>
                                                                                        <FaLayerGroup /> Approve Entire Batch
                                                                                    </button>
                                                                                    <button className="btn-sm btn-outline-danger" onClick={() => handleOpenRejectModal(null, record.bulkServiceReference)} disabled={workflowLoading}>
                                                                                        <FaLayerGroup /> Reject Entire Batch
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                    {record.approvalStatus === 'Approved' && (
                                                                        <span className="sr-status-final"><FaCalendarCheck /> Approved — no further action required</span>
                                                                    )}
                                                                    {record.approvalStatus === 'Rejected' && (
                                                                        <span className="sr-status-final sr-rejected-label"><FaExclamationTriangle /> Rejected — see reason above</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="empty-message">No service records yet.</p>
                                    )}
                                </div>
                            )}

                            {/* Audit Trail Tab */}
                            {detailTab === 'audit' && (
                                <div className="audit-content">
                                    <h3>Audit Trail</h3>
                                    {selectedAsset.auditLogs?.length > 0 ? (
                                        <div className="audit-timeline">
                                            {selectedAsset.auditLogs.map(log => (
                                                <div key={log.assetAuditLogId} className="audit-entry">
                                                    <div className="audit-timestamp">{new Date(log.timestamp).toLocaleString()}</div>
                                                    <div className="audit-action">{log.action}</div>
                                                    <div className="audit-details">{log.details}</div>
                                                    <div className="audit-user">{log.performedByUserName || 'System'}</div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="empty-message">No audit entries yet.</p>
                                    )}
                                </div>
                            )}

                            {/* Documents Tab */}
                            {detailTab === 'documents' && (
                                <div className="documents-content">
                                    <h3>Documents</h3>

                                    {/* Upload form */}
                                    <div className="document-upload-form">
                                        <div className="upload-controls">
                                            <select
                                                value={documentType}
                                                onChange={(e) => setDocumentType(e.target.value)}
                                                className="doc-type-select"
                                            >
                                                {referenceData?.documentTypes?.map(type => (
                                                    <option key={type} value={type}>{type.replace(/([A-Z])/g, ' $1').trim()}</option>
                                                ))}
                                            </select>
                                            <input
                                                type="text"
                                                placeholder="Notes (optional)"
                                                value={documentNotes}
                                                onChange={(e) => setDocumentNotes(e.target.value)}
                                                className="doc-notes-input"
                                            />
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                onChange={handleDocumentUpload}
                                                style={{ display: 'none' }}
                                                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.csv,.zip"
                                            />
                                            <button
                                                className="btn-upload"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={uploadingDocument}
                                            >
                                                {uploadingDocument ? <FaSpinner className="spin" /> : <FaUpload />}
                                                {uploadingDocument ? ' Uploading...' : ' Upload Document'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Document list */}
                                    {selectedAsset.documents?.length > 0 ? (
                                        <table className="data-table">
                                            <thead>
                                                <tr>
                                                    <th>File Name</th>
                                                    <th>Type</th>
                                                    <th>Size</th>
                                                    <th>Uploaded</th>
                                                    <th>Notes</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedAsset.documents.map(doc => (
                                                    <tr key={doc.assetDocumentId}>
                                                        <td>{doc.fileName}</td>
                                                        <td>
                                                            <span className="type-badge type-service">
                                                                {doc.documentType?.replace(/([A-Z])/g, ' $1').trim()}
                                                            </span>
                                                        </td>
                                                        <td>{doc.fileSizeBytes ? `${(doc.fileSizeBytes / 1024).toFixed(1)} KB` : '-'}</td>
                                                        <td>{new Date(doc.createdDate).toLocaleDateString()}</td>
                                                        <td>{doc.notes || '-'}</td>
                                                        <td>
                                                            <div className="action-buttons">
                                                                <button
                                                                    className="btn-icon"
                                                                    onClick={() => handleDocumentDownload(doc.assetDocumentId, doc.fileName)}
                                                                    title="Download"
                                                                >
                                                                    <FaDownload />
                                                                </button>
                                                                <button
                                                                    className="btn-icon btn-danger"
                                                                    onClick={() => handleDocumentDelete(doc.assetDocumentId)}
                                                                    title="Delete"
                                                                >
                                                                    <FaTrashAlt />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <p className="empty-message">No documents uploaded yet.</p>
                                    )}
                                </div>
                            )}

                            {/* Schedules Tab */}
                            {detailTab === 'schedules' && (
                                <div className="detail-content">
                                    <div className="section-header">
                                        <h3>Service Schedules</h3>
                                        <button
                                            className="btn-create"
                                            onClick={() => {
                                                setEditingScheduleId(null);
                                                setScheduleForm(getEmptyScheduleForm());
                                                setShowScheduleModal(true);
                                            }}
                                        >
                                            <FaPlus /> Add Schedule
                                        </button>
                                    </div>
                                    <p style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '1rem' }}>
                                        Define recurring service intervals for this asset. The background task will
                                        create Action Items automatically when each interval is approaching.
                                    </p>
                                    {schedulesLoading ? (
                                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                                            <FaSpinner className="fa-spin" size={24} />
                                        </div>
                                    ) : assetSchedules.length === 0 ? (
                                        <p className="empty-message">No service schedules configured for this asset.</p>
                                    ) : (
                                        <table className="data-table">
                                            <thead>
                                                <tr>
                                                    <th>Service Type</th>
                                                    <th>Interval</th>
                                                    <th>Advance Notice</th>
                                                    <th>Last Service</th>
                                                    <th>Next Due</th>
                                                    <th>Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {assetSchedules.map(s => (
                                                    <tr key={s.assetServiceScheduleId} className={s.isOverdue ? 'row-schedule-overdue' : s.daysUntilDue != null && s.daysUntilDue <= s.advanceNoticeDays ? 'row-schedule-due-soon' : ''}>
                                                        <td>
                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                                {s.serviceTypeColour && (
                                                                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: s.serviceTypeColour, display: 'inline-block', flexShrink: 0 }} />
                                                                )}
                                                                {s.serviceTypeName}
                                                            </span>
                                                        </td>
                                                        <td>Every {s.intervalValue} {s.intervalUnit}</td>
                                                        <td>{s.advanceNoticeDays} days</td>
                                                        <td>{s.lastServiceDate ? new Date(s.lastServiceDate).toLocaleDateString() : <em style={{ opacity: 0.5 }}>Never</em>}</td>
                                                        <td>{s.nextDueDate ? new Date(s.nextDueDate).toLocaleDateString() : <em style={{ opacity: 0.5 }}>N/A</em>}</td>
                                                        <td>
                                                            {s.isOverdue
                                                                ? <span className="service-overdue">Overdue</span>
                                                                : s.daysUntilDue != null && s.daysUntilDue <= s.advanceNoticeDays
                                                                    ? <span className="service-due-soon">Due Soon</span>
                                                                    : <span className="service-ok">OK</span>
                                                            }
                                                        </td>
                                                        <td>
                                                            <div className="action-buttons">
                                                                <button
                                                                    className="btn-icon"
                                                                    title="Edit"
                                                                    onClick={() => {
                                                                        setEditingScheduleId(s.assetServiceScheduleId);
                                                                        setScheduleForm({
                                                                            assetServiceTypeId: s.assetServiceTypeId,
                                                                            intervalValue: s.intervalValue,
                                                                            intervalUnit: s.intervalUnit,
                                                                            advanceNoticeDays: s.advanceNoticeDays,
                                                                            lastServiceDate: s.lastServiceDate ? s.lastServiceDate.slice(0, 10) : '',
                                                                            firstDueDate: s.firstDueDate ? s.firstDueDate.slice(0, 10) : '',
                                                                            notes: s.notes || ''
                                                                        });
                                                                        setShowScheduleModal(true);
                                                                    }}
                                                                ><FaEdit /></button>
                                                                <button
                                                                    className="btn-icon btn-danger"
                                                                    title="Remove"
                                                                    onClick={async () => {
                                                                        if (!window.confirm('Remove this service schedule?')) return;
                                                                        try {
                                                                            const res = await apiFetch(`/api/AssetManagement/service-schedules/${s.assetServiceScheduleId}`, { method: 'DELETE' });
                                                                            if (res.ok) {
                                                                                toast.success('Schedule removed');
                                                                                fetchAssetSchedules(selectedAsset.assetId);
                                                                            } else toast.error('Failed to remove schedule');
                                                                        } catch { toast.error('Failed to remove schedule'); }
                                                                    }}
                                                                ><FaTrashAlt /></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Add / Edit Service Schedule Modal */}
            {showScheduleModal && selectedAsset && (
                <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
                    <div className="modal-dialog" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingScheduleId ? 'Edit Service Schedule' : 'Add Service Schedule'}</h2>
                            <button className="btn-close" onClick={() => setShowScheduleModal(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-grid">
                                <div className="form-group full-width">
                                    <label>Service Type <span className="required">*</span></label>
                                    <select
                                        value={scheduleForm.assetServiceTypeId}
                                        onChange={e => setScheduleForm(f => ({ ...f, assetServiceTypeId: e.target.value }))}
                                    >
                                        <option value="">Select type...</option>
                                        {serviceTypes.map(st => (
                                            <option key={st.assetServiceTypeId} value={st.assetServiceTypeId}>{st.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Interval <span className="required">*</span></label>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <input
                                            type="number"
                                            min={1}
                                            style={{ width: 80 }}
                                            value={scheduleForm.intervalValue}
                                            onChange={e => setScheduleForm(f => ({ ...f, intervalValue: e.target.value }))}
                                        />
                                        <select
                                            value={scheduleForm.intervalUnit}
                                            onChange={e => setScheduleForm(f => ({ ...f, intervalUnit: e.target.value }))}
                                        >
                                            {['Days', 'Weeks', 'Months', 'Years'].map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Advance Notice (days)</label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={scheduleForm.advanceNoticeDays}
                                        onChange={e => setScheduleForm(f => ({ ...f, advanceNoticeDays: e.target.value }))}
                                        style={{ width: 100 }}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Last Service Date</label>
                                    <input
                                        type="date"
                                        value={scheduleForm.lastServiceDate}
                                        onChange={e => setScheduleForm(f => ({ ...f, lastServiceDate: e.target.value }))}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>First Due Date <span style={{ color: '#718096', fontWeight: 400 }}>(override)</span></label>
                                    <input
                                        type="date"
                                        value={scheduleForm.firstDueDate}
                                        onChange={e => setScheduleForm(f => ({ ...f, firstDueDate: e.target.value }))}
                                    />
                                    <small style={{ color: '#718096' }}>
                                        If set, overrides interval calculation for the first due date.
                                    </small>
                                </div>
                                <div className="form-group full-width">
                                    <label>Notes</label>
                                    <textarea
                                        rows={2}
                                        value={scheduleForm.notes}
                                        onChange={e => setScheduleForm(f => ({ ...f, notes: e.target.value }))}
                                        placeholder="Optional notes about this schedule..."
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowScheduleModal(false)} disabled={scheduleModalSaving}>
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                disabled={scheduleModalSaving || !scheduleForm.assetServiceTypeId}
                                onClick={async () => {
                                    if (!scheduleForm.assetServiceTypeId) { toast.error('Service type is required'); return; }
                                    if (!scheduleForm.intervalValue || parseInt(scheduleForm.intervalValue) <= 0) { toast.error('Interval must be greater than zero'); return; }
                                    setScheduleModalSaving(true);
                                    try {
                                        const payload = {
                                            assetId: selectedAsset.assetId,
                                            assetServiceTypeId: parseInt(scheduleForm.assetServiceTypeId),
                                            intervalValue: parseInt(scheduleForm.intervalValue),
                                            intervalUnit: scheduleForm.intervalUnit,
                                            advanceNoticeDays: parseInt(scheduleForm.advanceNoticeDays) || 30,
                                            lastServiceDate: scheduleForm.lastServiceDate || null,
                                            firstDueDate: scheduleForm.firstDueDate || null,
                                            notes: scheduleForm.notes || null
                                        };
                                        const url = editingScheduleId
                                            ? `/api/AssetManagement/service-schedules/${editingScheduleId}`
                                            : '/api/AssetManagement/service-schedules';
                                        const method = editingScheduleId ? 'PUT' : 'POST';
                                        const res = await apiFetch(url, {
                                            method,
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify(payload)
                                        });
                                        if (res.ok) {
                                            toast.success(editingScheduleId ? 'Schedule updated' : 'Schedule added');
                                            setShowScheduleModal(false);
                                            fetchAssetSchedules(selectedAsset.assetId);
                                        } else {
                                            const err = await res.json().catch(() => ({}));
                                            toast.error(err.message || 'Failed to save schedule');
                                        }
                                    } catch (e) {
                                        Sentry.captureException(e);
                                        toast.error('Failed to save schedule');
                                    } finally {
                                        setScheduleModalSaving(false);
                                    }
                                }}
                            >
                                {scheduleModalSaving ? <><FaSpinner className="fa-spin" /> Saving…</> : <><FaSave /> Save</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Create Service Record Modal */}
            {showServiceModal && (
                <div className="modal-overlay" onClick={() => setShowServiceModal(false)}>
                    <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Add Service Record</h2>
                            <button className="btn-close" onClick={() => setShowServiceModal(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Record Type *</label>
                                    <select value={serviceFormData.recordType} onChange={(e) => setServiceFormData({ ...serviceFormData, recordType: e.target.value })}>
                                        {referenceData?.serviceRecordTypes?.map(t => (
                                            <option key={t} value={t}>{t === 'RepairAndMaintenance' ? 'Repair & Maintenance' : 'Scheduled Service'}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group full-width">
                                    <label>Title *</label>
                                    <input type="text" value={serviceFormData.title} onChange={(e) => setServiceFormData({ ...serviceFormData, title: e.target.value })} placeholder="Brief description of the service" />
                                </div>
                                <div className="form-group full-width">
                                    <label>Description</label>
                                    <textarea rows={3} value={serviceFormData.description} onChange={(e) => setServiceFormData({ ...serviceFormData, description: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Service Due Date</label>
                                    <input type="date" value={serviceFormData.serviceDueDate} onChange={(e) => setServiceFormData({ ...serviceFormData, serviceDueDate: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Service Date</label>
                                    <input type="date" value={serviceFormData.serviceDate} onChange={(e) => setServiceFormData({ ...serviceFormData, serviceDate: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Service Provider</label>
                                    <input type="text" value={serviceFormData.serviceProviderName} onChange={(e) => setServiceFormData({ ...serviceFormData, serviceProviderName: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Provider Reference</label>
                                    <input type="text" value={serviceFormData.providerReference} onChange={(e) => setServiceFormData({ ...serviceFormData, providerReference: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Cost (ZAR)</label>
                                    <input type="number" step="0.01" min={0} value={serviceFormData.cost} onChange={(e) => setServiceFormData({ ...serviceFormData, cost: e.target.value })} placeholder="0.00" />
                                    <label className="checkbox-label vat-checkbox">
                                        <input type="checkbox" checked={serviceFormData.costIncludesVat} onChange={(e) => setServiceFormData({ ...serviceFormData, costIncludesVat: e.target.checked })} />
                                        Includes VAT
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label>Cost Notes</label>
                                    <input type="text" value={serviceFormData.costNotes} onChange={(e) => setServiceFormData({ ...serviceFormData, costNotes: e.target.value })} placeholder="Cost breakdown or notes" />
                                </div>
                                <div className="form-group full-width">
                                    <label>Notes</label>
                                    <textarea rows={2} value={serviceFormData.notes} onChange={(e) => setServiceFormData({ ...serviceFormData, notes: e.target.value })} />
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowServiceModal(false)} disabled={saving}>Cancel</button>
                            <button className="btn-primary" onClick={handleSaveServiceRecord} disabled={saving}>
                                {saving ? <><FaSpinner className="fa-spin" /> Saving...</> : 'Create Record'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create/Edit Group Modal */}
            {showGroupModal && (
                <div className="modal-overlay" onClick={() => setShowGroupModal(false)}>
                    <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{groupFormData.assetGroupId ? 'Edit Group' : 'Create Asset Group'}</h2>
                            <button className="btn-close" onClick={() => setShowGroupModal(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-grid">
                                <div className="form-group full-width">
                                    <label>Group Name *</label>
                                    <input type="text" value={groupFormData.name} onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })} placeholder="e.g. Fire Extinguishers - Level 2" />
                                </div>
                                <div className="form-group full-width">
                                    <label>Description</label>
                                    <textarea rows={2} value={groupFormData.description} onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })} placeholder="Describe what this group covers" />
                                </div>
                                <div className="form-group">
                                    <label>Property *</label>
                                    <select value={groupFormData.propertyId} onChange={(e) => setGroupFormData({ ...groupFormData, propertyId: e.target.value })} disabled={!!groupFormData.assetGroupId}>
                                        <option value="">Select Property</option>
                                        {properties.map(p => (
                                            <option key={p.propertyID || p.propertyId} value={p.propertyID || p.propertyId}>{p.propertyName}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Category (optional)</label>
                                    <select value={groupFormData.assetCategoryId} onChange={(e) => setGroupFormData({ ...groupFormData, assetCategoryId: e.target.value })}>
                                        <option value="">All Categories</option>
                                        {categories.filter(c => c.isActive).map(c => (
                                            <option key={c.assetCategoryId} value={c.assetCategoryId}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Fungible Quantity</label>
                                    <input type="number" min={0} value={groupFormData.quantity} onChange={(e) => setGroupFormData({ ...groupFormData, quantity: e.target.value })} placeholder="e.g. 50 (for items not individually tracked)" />
                                    <span className="field-help">For items like fire extinguishers without serial numbers — the total count in the group.</span>
                                </div>
                                <div className="form-group full-width">
                                    <label>Notes</label>
                                    <textarea rows={2} value={groupFormData.notes} onChange={(e) => setGroupFormData({ ...groupFormData, notes: e.target.value })} />
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowGroupModal(false)} disabled={saving}>Cancel</button>
                            <button className="btn-primary" onClick={handleSaveGroup} disabled={saving}>
                                {saving ? <><FaSpinner className="fa-spin" /> Saving...</> : (groupFormData.assetGroupId ? 'Update Group' : 'Create Group')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Service Record Modal */}
            {showBulkServiceModal && selectedGroup && (
                <div className="modal-overlay" onClick={() => setShowBulkServiceModal(false)}>
                    <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2><FaLayerGroup /> Bulk Service Record — {selectedGroup.name}</h2>
                            <button className="btn-close" onClick={() => setShowBulkServiceModal(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            <p className="info-text">
                                This will create a service record for each asset in the group.
                                Uncheck any assets you want to exclude from this service event.
                            </p>

                            {/* Member Selection */}
                            {selectedGroup.members?.length > 0 && (
                                <div className="bulk-member-selection">
                                    <h4>Assets to Service ({selectedGroup.members.length - excludedAssetIds.length} of {selectedGroup.members.length})</h4>
                                    <table className="data-table compact">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40px' }}>Include</th>
                                                <th>Asset</th>
                                                <th>Tag / Serial</th>
                                                <th>Location</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedGroup.members.map(member => (
                                                <tr key={member.assetGroupMemberId} className={excludedAssetIds.includes(member.assetId) ? 'excluded-row' : ''}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={!excludedAssetIds.includes(member.assetId)}
                                                            onChange={() => toggleExcludeAsset(member.assetId)}
                                                        />
                                                    </td>
                                                    <td>{member.assetName}</td>
                                                    <td>{member.assetTag || member.serialNumber || '-'}</td>
                                                    <td>{member.location || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {selectedGroup.quantity > 0 && !selectedGroup.members?.length && (
                                <div className="info-callout">
                                    <FaInfoCircle /> This group has {selectedGroup.quantity} fungible items (not individually tracked).
                                    A single service record will be created against the group.
                                </div>
                            )}

                            {/* Service Details Form */}
                            <div className="form-grid" style={{ marginTop: '1rem' }}>
                                <div className="form-group">
                                    <label>Record Type *</label>
                                    <select value={bulkServiceFormData.recordType} onChange={(e) => setBulkServiceFormData({ ...bulkServiceFormData, recordType: e.target.value })}>
                                        {referenceData?.serviceRecordTypes?.map(t => (
                                            <option key={t} value={t}>{t === 'RepairAndMaintenance' ? 'Repair & Maintenance' : 'Scheduled Service'}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group full-width">
                                    <label>Title *</label>
                                    <input type="text" value={bulkServiceFormData.title} onChange={(e) => setBulkServiceFormData({ ...bulkServiceFormData, title: e.target.value })} placeholder="e.g. Annual Fire Extinguisher Inspection Q1 2026" />
                                </div>
                                <div className="form-group full-width">
                                    <label>Description</label>
                                    <textarea rows={3} value={bulkServiceFormData.description} onChange={(e) => setBulkServiceFormData({ ...bulkServiceFormData, description: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Service Due Date</label>
                                    <input type="date" value={bulkServiceFormData.serviceDueDate} onChange={(e) => setBulkServiceFormData({ ...bulkServiceFormData, serviceDueDate: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Service Date</label>
                                    <input type="date" value={bulkServiceFormData.serviceDate} onChange={(e) => setBulkServiceFormData({ ...bulkServiceFormData, serviceDate: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Service Provider</label>
                                    <input type="text" value={bulkServiceFormData.serviceProviderName} onChange={(e) => setBulkServiceFormData({ ...bulkServiceFormData, serviceProviderName: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Provider Reference</label>
                                    <input type="text" value={bulkServiceFormData.providerReference} onChange={(e) => setBulkServiceFormData({ ...bulkServiceFormData, providerReference: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Cost (ZAR)</label>
                                    <input type="number" step="0.01" min={0} value={bulkServiceFormData.cost} onChange={(e) => setBulkServiceFormData({ ...bulkServiceFormData, cost: e.target.value })} placeholder="0.00" />
                                    <label className="checkbox-label vat-checkbox">
                                        <input type="checkbox" checked={bulkServiceFormData.costIncludesVat} onChange={(e) => setBulkServiceFormData({ ...bulkServiceFormData, costIncludesVat: e.target.checked })} />
                                        Includes VAT
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label>Cost Notes</label>
                                    <input type="text" value={bulkServiceFormData.costNotes} onChange={(e) => setBulkServiceFormData({ ...bulkServiceFormData, costNotes: e.target.value })} placeholder="Cost breakdown or notes" />
                                </div>
                                <div className="form-group full-width">
                                    <label>Notes</label>
                                    <textarea rows={2} value={bulkServiceFormData.notes} onChange={(e) => setBulkServiceFormData({ ...bulkServiceFormData, notes: e.target.value })} />
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowBulkServiceModal(false)} disabled={saving}>Cancel</button>
                            <button className="btn-primary" onClick={handleSaveBulkServiceRecord} disabled={saving}>
                                {saving ? <><FaSpinner className="fa-spin" /> Creating...</> : `Create ${selectedGroup.members ? selectedGroup.members.length - excludedAssetIds.length : 0} Service Record(s)`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rejection Reason Modal */}
            {showRejectModal && (
                <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
                    <div className="modal-dialog modal-sm" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2><FaExclamationTriangle /> Reject Service Record</h2>
                            <button className="btn-close" onClick={() => setShowRejectModal(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            {rejectTargetBulkRef && (
                                <p className="info-text"><FaLayerGroup /> This will reject all records in the bulk batch.</p>
                            )}
                            <div className="form-group full-width">
                                <label>Reason for Rejection <span className="required">*</span></label>
                                <textarea
                                    rows={4}
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    placeholder="Provide a clear reason for rejection..."
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowRejectModal(false)} disabled={workflowLoading}>Cancel</button>
                            <button className="btn-danger" onClick={handleConfirmReject} disabled={workflowLoading || !rejectionReason.trim()}>
                                {workflowLoading ? <><FaSpinner className="fa-spin" /> Rejecting...</> : 'Confirm Rejection'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </DndContext>
    );
};

// ── Drag-and-Drop Sub-Components ─────────────────────────────────────────────
//
// These are defined as named function declarations outside AssetManagementPage
// rather than inline components so they are not re-created on every parent
// render.  React would unmount and remount them on every render if they were
// defined inside the parent component body.

/**
 * DraggableAssetRow
 *
 * Renders a single <tr> (and optionally a second <tr> for the quick-service
 * inline form) inside the asset table.
 *
 * @dnd-kit integration:
 *   - useDraggable registers the row with the DndContext as draggable.
 *   - `id` uses the "asset-{assetId}" convention so handleDragEnd can parse
 *     the asset ID from the dnd-kit event without needing a lookup.
 *   - `transform` is applied as a CSS translate so the row visually moves
 *     during the drag.  `opacity: 0.4` on the source row hints to the user
 *     that the original position is the drop-origin.
 *   - The grip column (`showDragHandle`) is only rendered when the sidebar is
 *     open, a property is selected, and groups exist — otherwise drag is not
 *     useful and the column would waste space.
 *
 * Row class names:
 *   - inactive-row: asset.isActive === false (greyed out)
 *   - selected-row: asset is checked in the multi-select toolbar
 *   - dragging-row: this row is being dragged (opacity already applied via
 *     inline style; class can add additional CSS effects)
 *   - row-schedule-overdue / row-schedule-due-soon: from scheduleOverdueMap
 *
 * The quick-service form renders as a second <tr> beneath the asset row when
 * `quickServiceAssetId === asset.assetId`.  It shares `serviceFormData` and
 * `setServiceFormData` state with the parent so both the modal service form
 * and the inline form read/write the same form fields.
 *
 * Props:
 *   asset              - Asset row DTO from the server
 *   serviceStatus      - { label, className } from getServiceStatus()
 *   isSelected         - Whether the multi-select checkbox is checked
 *   onToggleSelect     - Handler to toggle individual selection
 *   onView             - Opens the full detail modal
 *   onEdit             - Opens the create/edit modal pre-filled with this asset
 *   onToggleActive     - Deactivates or reactivates the asset
 *   onQuickService     - Opens the inline quick-service form for this asset
 *   getStatusBadgeClass - CSS class mapper for the status badge
 *   showDragHandle     - Whether to render the grip icon drag column
 *   quickServiceAssetId - Currently open quick-service asset ID (from parent)
 *   serviceFormData    - Shared service form state
 *   setServiceFormData - Setter for shared service form state
 *   referenceData      - Server enum lists (for record type select)
 *   saving             - True while an API call is in-flight
 *   onSaveQuickService - Saves the quick-service record
 *   onCancelQuickService - Closes the quick-service form without saving
 *   scheduleOverdue    - { isOverdue, isDueSoon } from scheduleOverdueMap
 */
function DraggableAssetRow({
    asset, serviceStatus, isSelected, onToggleSelect, onView, onEdit,
    onToggleActive, onQuickService, getStatusBadgeClass, showDragHandle,
    quickServiceAssetId, serviceFormData, setServiceFormData, referenceData,
    saving, onSaveQuickService, onCancelQuickService, scheduleOverdue
}) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: `asset-${asset.assetId}`,
        data: { type: 'asset', asset }
    });

    const style = transform
        ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.4 : 1 }
        : undefined;

    const isQuickServiceOpen = quickServiceAssetId === asset.assetId;

    return (
        <>
            <tr
                ref={setNodeRef}
                style={style}
                className={`${!asset.isActive ? 'inactive-row' : ''} ${isSelected ? 'selected-row' : ''} ${isDragging ? 'dragging-row' : ''} ${scheduleOverdue?.isOverdue ? 'row-schedule-overdue' : scheduleOverdue?.isDueSoon ? 'row-schedule-due-soon' : ''}`}
            >
                <td className="checkbox-col">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={onToggleSelect}
                    />
                </td>
                {showDragHandle && (
                    <td className="drag-col" {...attributes} {...listeners}>
                        <FaGripVertical className="drag-handle" />
                    </td>
                )}
                <td>
                    <div className="asset-name-cell">
                        <strong>{asset.name}</strong>
                        {asset.assetTag && <span className="asset-tag"><FaTag /> {asset.assetTag}</span>}
                        {asset.quantity > 1 && <span className="qty-badge">×{asset.quantity}</span>}
                    </div>
                </td>
                <td>{asset.propertyName || '-'}</td>
                <td>
                    {asset.categoryName || '-'}
                    {asset.subCategoryName && <span className="sub-category"> / {asset.subCategoryName}</span>}
                </td>
                <td>{asset.location || '-'}</td>
                <td>
                    <span className={`status-badge ${getStatusBadgeClass(asset.status)}`}>
                        {asset.status}
                    </span>
                </td>
                <td>
                    <span className={`service-status ${serviceStatus.className}`}>
                        {serviceStatus.label}
                    </span>
                </td>
                <td>
                    {asset.hasWarranty ? (
                        <span className={`warranty-badge ${asset.warrantyExpiryDate && new Date(asset.warrantyExpiryDate) < new Date() ? 'warranty-expired' : 'warranty-active'}`}>
                            {asset.warrantyExpiryDate
                                ? new Date(asset.warrantyExpiryDate).toLocaleDateString()
                                : 'Active'}
                        </span>
                    ) : (
                        <span className="warranty-none">None</span>
                    )}
                </td>
                <td className="action-buttons">
                    <button className="btn-icon" onClick={onView} title="View Details"><FaEye /></button>
                    <button className="btn-icon" onClick={onEdit} title="Edit"><FaEdit /></button>
                    <button className="btn-icon btn-service" onClick={onQuickService} title="Quick Service Record">
                        <FaWrench />
                    </button>
                    <button
                        className={`btn-icon ${asset.isActive ? 'btn-danger' : 'btn-success'}`}
                        onClick={onToggleActive}
                        title={asset.isActive ? 'Deactivate' : 'Reactivate'}
                    >
                        {asset.isActive ? <FaTrashAlt /> : <FaUndo />}
                    </button>
                </td>
            </tr>
            {/* Inline Quick Service Form */}
            {isQuickServiceOpen && (
                <tr className="quick-service-row">
                    <td colSpan={showDragHandle ? 10 : 9}>
                        <div className="quick-service-form">
                            <div className="qs-header">
                                <FaBolt /> <strong>Quick Service Record</strong> for {asset.name}
                                <button className="btn-icon" onClick={onCancelQuickService}><FaTimes /></button>
                            </div>
                            <div className="qs-fields">
                                <select value={serviceFormData.recordType} onChange={(e) => setServiceFormData(prev => ({ ...prev, recordType: e.target.value }))}>
                                    {referenceData?.serviceRecordTypes?.map(t => (
                                        <option key={t} value={t}>{t === 'RepairAndMaintenance' ? 'Repair & Maintenance' : 'Scheduled Service'}</option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    placeholder="Title *"
                                    value={serviceFormData.title}
                                    onChange={(e) => setServiceFormData(prev => ({ ...prev, title: e.target.value }))}
                                    autoFocus
                                />
                                <input
                                    type="text"
                                    placeholder="Service Provider"
                                    value={serviceFormData.serviceProviderName}
                                    onChange={(e) => setServiceFormData(prev => ({ ...prev, serviceProviderName: e.target.value }))}
                                />
                                <input
                                    type="date"
                                    value={serviceFormData.serviceDate}
                                    onChange={(e) => setServiceFormData(prev => ({ ...prev, serviceDate: e.target.value }))}
                                    title="Service Date"
                                />
                                <input
                                    type="number"
                                    step="0.01"
                                    min={0}
                                    placeholder="Cost (ZAR)"
                                    value={serviceFormData.cost}
                                    onChange={(e) => setServiceFormData(prev => ({ ...prev, cost: e.target.value }))}
                                />
                                <div className="qs-actions">
                                    <button className="btn-sm btn-primary" onClick={onSaveQuickService} disabled={saving}>
                                        {saving ? <FaSpinner className="fa-spin" /> : <FaPlus />} Create
                                    </button>
                                    <button className="btn-sm btn-secondary" onClick={onCancelQuickService}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

/**
 * DroppableGroupCard
 *
 * Renders a collapsible card in the left sidebar for a single asset group.
 * Registered as a drop target via useDroppable so that dragged asset rows
 * can be dropped onto it to add them to the group.
 *
 * @dnd-kit integration:
 *   - `id` uses the "group-{assetGroupId}" convention matching handleDragEnd.
 *   - `isOver` is true while an active drag is hovering over this card;
 *     the `drop-hover` CSS class provides a visual highlight.
 *   - `isDragActive` (from parent activeDragId) makes the drop zone label
 *     visible on ALL group cards while any drag is in progress, not just
 *     the one being hovered. This guides the user to valid drop targets.
 *
 * Expansion behaviour:
 *   - Clicking the header toggles `isExpanded` (managed by parent via
 *     `expandedGroupId` state).  Only one card is expanded at a time.
 *   - When expanded and `selectedGroup` matches this group, the full member
 *     list is shown.  If `selectedGroup` is not yet loaded (e.g. the group
 *     was just expanded), the parent calls handleViewGroup to load it.
 *
 * Props:
 *   group           - Group summary DTO (name, memberCount, categoryName, quantity)
 *   isExpanded      - Whether this card is currently expanded
 *   selectedGroup   - Fully-loaded group detail (with members) or null
 *   onToggleExpand  - Called when the header is clicked
 *   onEdit          - Opens the edit group modal
 *   onBulkService   - Opens the bulk service modal for this group
 *   onRemoveMember  - Removes a single member asset by assetId
 *   isDragActive    - True when any asset row is being dragged
 */
function DroppableGroupCard({
    group, isExpanded, selectedGroup, onToggleExpand,
    onEdit, onBulkService, onRemoveMember, isDragActive
}) {
    const { isOver, setNodeRef } = useDroppable({
        id: `group-${group.assetGroupId}`,
        data: { type: 'group', group }
    });

    const members = isExpanded && selectedGroup?.assetGroupId === group.assetGroupId
        ? selectedGroup.members || []
        : [];

    return (
        <div
            ref={setNodeRef}
            className={`group-card ${isOver ? 'drop-hover' : ''} ${isDragActive ? 'drop-ready' : ''} ${isExpanded ? 'expanded' : ''}`}
        >
            <div className="group-card-header" onClick={onToggleExpand}>
                <div className="group-card-title">
                    <span className="expand-toggle">{isExpanded ? <FaChevronDown /> : <FaChevronRight />}</span>
                    <strong>{group.name}</strong>
                    <span className="member-count">{group.memberCount}</span>
                </div>
                <div className="group-card-meta">
                    <span className="group-category">{group.categoryName || 'All'}</span>
                    {group.quantity > 0 && <span className="group-qty">×{group.quantity}</span>}
                </div>
            </div>

            {isDragActive && (
                <div className={`drop-zone ${isOver ? 'drop-zone-active' : ''}`}>
                    <FaPlus /> Drop here to add
                </div>
            )}

            {isExpanded && (
                <div className="group-card-body">
                    {group.description && <p className="group-desc">{group.description}</p>}
                    <div className="group-card-actions">
                        <button className="btn-sm btn-secondary" onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Edit Group">
                            <FaEdit /> Edit
                        </button>
                        <button className="btn-sm btn-primary" onClick={(e) => { e.stopPropagation(); onBulkService(); }} title="Bulk Service">
                            <FaToolbox /> Bulk Service
                        </button>
                    </div>

                    {members.length > 0 ? (
                        <div className="group-members-list">
                            <h5>Members ({members.length})</h5>
                            {members.map(member => (
                                <div key={member.assetGroupMemberId} className="member-chip">
                                    <span className="member-name">{member.assetName}</span>
                                    {member.assetTag && <span className="member-tag">{member.assetTag}</span>}
                                    <button
                                        className="member-remove"
                                        onClick={(e) => { e.stopPropagation(); onRemoveMember(member.assetId); }}
                                        title="Remove from group"
                                    >
                                        <FaTimes />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="no-members">No members. Drag assets here to add.</p>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Helper Functions ─────────────────────────────────────────────────────────
//
// All getEmpty*() functions live at module scope (outside the component) so
// they can be called during useState initialisation and from handlers without
// creating a function reference inside the component.  They return plain
// objects (not state) so callers can freely mutate the result before passing
// it to a state setter.

/**
 * Returns a blank asset form data object.
 *
 * Field names exactly match the AssetRequest DTO on the server so the object
 * can be spread directly into the POST/PUT payload after numeric conversions.
 * Adding a new field here automatically includes it in handleSaveAsset without
 * any other changes.
 *
 * Sentinel values:
 *   - Empty string ('') for text inputs — controlled input pattern.
 *   - null for IDs that are optional foreign keys.
 *   - false for boolean checkboxes.
 *   - 1 for quantity so the field is pre-filled with the most common value.
 *
 * @returns {Object} Empty form data for asset create/edit
 */
function getEmptyFormData() {
    return {
        assetId: null,
        name: '',
        assetTag: '',
        serialNumber: '',
        manufacturer: '',
        model: '',
        description: '',
        propertyId: '',
        belongsToTenant: false,
        tenantName: '',
        mdaTenantId: null,
        location: '',
        mdaRentableUnitId: null,
        rentableUnitName: '',
        assetCategoryId: '',
        assetSubCategoryId: '',
        isNew: false,
        installationDate: '',
        commissioningDate: '',
        requiresServicing: false,
        serviceIntervalValue: '',
        serviceIntervalUnit: '',
        lastServiceDate: '',
        hasWarranty: false,
        warrantyExpiryDate: '',
        warrantyNotes: '',
        purchaseCost: '',
        purchaseCostIncludesVat: false,
        quantity: 1,
        notes: '',
        assetOwnerId: '',
        assetManagerId: ''
    };
}

/**
 * Returns a blank service record form data object.
 *
 * `recordType` defaults to 'ScheduledService' (the most common type).
 * Used by both the full service record modal and the quick-service inline form.
 *
 * @returns {Object} Empty form data for service record creation
 */
function getEmptyServiceForm() {
    return {
        recordType: 'ScheduledService',
        title: '',
        description: '',
        serviceDueDate: '',
        serviceDate: '',
        serviceProviderName: '',
        providerReference: '',
        cost: '',
        costIncludesVat: false,
        costNotes: '',
        notes: ''
    };
}

/**
 * Returns a blank asset group form data object.
 *
 * `propertyId` is pre-filled in handleCreateGroup from the active filter,
 * which is why it is kept as a separate field rather than being inferred.
 *
 * @returns {Object} Empty form data for group create/edit
 */
function getEmptyGroupForm() {
    return {
        assetGroupId: null,
        name: '',
        description: '',
        propertyId: '',
        assetCategoryId: '',
        quantity: '',
        notes: ''
    };
}

/**
 * Returns a blank bulk service record form data object.
 *
 * Intentionally identical shape to getEmptyServiceForm() so that the same
 * form grid JSX can be reused in the bulk service modal with minimal changes.
 *
 * @returns {Object} Empty form data for bulk service record creation
 */
function getEmptyBulkServiceForm() {
    return {
        recordType: 'ScheduledService',
        title: '',
        description: '',
        serviceDueDate: '',
        serviceDate: '',
        serviceProviderName: '',
        providerReference: '',
        cost: '',
        costIncludesVat: false,
        costNotes: '',
        notes: ''
    };
}

/**
 * Returns a blank service schedule form data object.
 *
 * Default values are opinionated: 6-month interval and 30-day advance notice
 * are sensible starting points for most property maintenance schedules.
 * Users can override them freely in the modal.
 *
 * `firstDueDate` is an optional override for the first due date and defaults
 * to empty (server will calculate it from lastServiceDate + intervalValue).
 *
 * @returns {Object} Empty form data for schedule create/edit
 */
function getEmptyScheduleForm() {
    return {
        assetServiceTypeId: '',
        intervalValue: 6,
        intervalUnit: 'Months',
        advanceNoticeDays: 30,
        lastServiceDate: '',
        firstDueDate: '',
        notes: ''
    };
}

export default AssetManagementPage;
